/**
 * =========================================================================
 * 🛡️ ARCHITECTURAL PRESERVATION GUARD — DO NOT COMPROMISE OR MODIFY CONTRACT
 * -------------------------------------------------------------------------
 * MANDATORY REQUIREMENT: Universal Header (<SiteNav>) & Footer (<SiteFooter>) Parity.
 * This component is the SINGLE SOURCE OF TRUTH for the site footer across ALL pages:
 * LandingPage, ArticlesPage, ArticleView, ProductPage, ShopPage, CartPage, PageView,
 * PromotionalPage, SearchPage, NotFoundPage, and EcommercePaywall.
 * 
 * CORE RULES:
 * 1. ZERO REGRESSIONS: Never remove, break, or diverge footer styling, background
 *    controls, column management, inline text editing, or live customizer bindings.
 * 2. CONTRACT INTEGRITY: Always maintain support for:
 *    - footer, socials, settings, config, typography, modules
 *    - getFontStyles, getSideBorder, getShadow, Editable
 *    - isAuthenticated, isEditing, onUpdate, onSetConfig, onSaveConfig
 *    - onAddCol, onRemoveCol, onMoveCol
 * 3. DEFENSIVE RESOLUTION: Always preserve internal fallbacks to config.settings,
 *    config.footer, and config.socials so uninitialized parent states never break the layout.
 * =========================================================================
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MediaInput from './MediaInput';
import { getSocialIcon as defaultGetSocialIcon } from '../utils/socialIcons.jsx';
import { isVideo } from '../utils/helpers.js';
import EditableComponent from './Editable.jsx';
import NewsletterWidget from './NewsletterWidget.jsx';
import { InlineSearchBar } from './SearchTool.jsx';
import DraggableToolbar from './DraggableToolbar';
import { toValidHex } from './StyleControls';
import { ColumnTypographyControls } from './ColumnTypographyControls.jsx';
import { LuPalette, LuSettings, LuTrash2 } from 'react-icons/lu';
import ReturnToTop from './ReturnToTop';
import { CurrencySelector } from '../ecommerce/CurrencySelector.jsx';
import { isEcommerceUnlocked } from '../ecommerce/branding.js';
import { FONT_OPTIONS, getFontOptions } from '../config/fontOptions.js';
import { resolveMediaUrl, normalizeAppUrl, getTenantFullUrl, handleAppUrlClick, stripHtml, addUnit, getFontStyles as getFontStylesHelper, getSideBorder as getSideBorderHelper, getShadow as getShadowHelper, isFileUrl, isExternalOrFileUrl } from '../utils/styleHelpers.js';

// --- Shared style constants for settings panels ---
const settingLabel = {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#64748b',
    display: 'block',
    marginBottom: '3px',
    textTransform: 'none',
    letterSpacing: '0.02em',
};

const settingInput = {
    width: '100%',
    minWidth: 0,
    padding: '4px 6px',
    border: '1px solid #cbd5e1',
    borderRadius: '4px',
    background: '#f8fafc',
    color: '#1e293b',
    fontSize: '11px',
    height: '28px',
    boxSizing: 'border-box',
};

const tabBtnStyle = (active) => ({
    padding: '5px 8px',
    fontSize: '10px',
    fontWeight: active ? '700' : '500',
    color: active ? '#2563eb' : '#64748b',
    background: active ? '#ffffff' : 'transparent',
    border: active ? '1px solid #cbd5e1' : '1px solid transparent',
    borderBottom: active ? '1px solid #ffffff' : '1px solid transparent',
    borderRadius: '4px 4px 0 0',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
    marginBottom: '-1px',
});

const ctrlBtn = (active = false, danger = false) => ({
    background: danger
        ? '#ef4444'
        : active
            ? '#3b82f6'
            : '#f1f5f9',
    color: active || danger ? 'white' : '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: '4px',
    padding: '3px 8px',
    fontSize: '12px',
    cursor: 'pointer',
    lineHeight: '18px',
    fontWeight: 'bold',
});

const SiteFooter = ({
    footer: footerProp,
    socials: socialsProp,
    settings: settingsProp,
    config = {},
    isAuthenticated,
    isEditing,
    getSideBorder,
    getFontStyles,
    getShadow,
    getSocialIcon = defaultGetSocialIcon,
    Editable: EditableProp,
    onUpdate,
    onSetConfig,
    onSaveConfig,
    onAddCol,
    onRemoveCol,
    onMoveCol,
}) => {
    const settings = (settingsProp && Object.keys(settingsProp).length > 0) ? settingsProp : (config?.settings || {});
    const footer = (Array.isArray(footerProp) && footerProp.length > 0) ? footerProp : (config?.footer || []);
    const socials = (Array.isArray(socialsProp) && socialsProp.length > 0) ? socialsProp : (config?.socials || []);
    const _getFontStyles = getFontStyles || (typeof config?.typography === 'object' ? getFontStylesHelper(config.typography) : () => ({}));
    const _getSideBorder = getSideBorder || (typeof settings === 'object' ? getSideBorderHelper(settings) : () => 'none');
    const _getShadow = getShadow || (typeof settings === 'object' ? getShadowHelper(settings) : () => 'none');

    const handleAddCol = (type = 'text') => {
        if (typeof onAddCol === 'function') {
            onAddCol(type);
        } else {
            const newCol = type === 'socials'
                ? { heading: 'Follow Us', type: 'socials', width: 1 }
                : type === 'links'
                ? { heading: 'Quick Links', type: 'links', width: 1, links: [{ label: 'Home', url: '/' }] }
                : { heading: 'New Column', text: 'Add content here...', type: 'text', width: 1 };
            const nextFooter = [...(footer || []), newCol];
            update(['footer'], nextFooter);
        }
    };

    const handleRemoveCol = (fIndex) => {
        if (typeof onRemoveCol === 'function') {
            onRemoveCol(fIndex);
        } else {
            const nextFooter = (footer || []).filter((_, i) => i !== fIndex);
            update(['footer'], nextFooter);
        }
    };

    const handleMoveCol = (fromIndex, toIndex) => {
        if (typeof onMoveCol === 'function') {
            onMoveCol(fromIndex, toIndex);
        } else {
            if (toIndex < 0 || toIndex >= (footer || []).length) return;
            const nextFooter = [...(footer || [])];
            const [movedCol] = nextFooter.splice(fromIndex, 1);
            nextFooter.splice(toIndex, 0, movedCol);
            update(['footer'], nextFooter);
        }
    };

    const fontOptions = getFontOptions(config?.customFonts || settings?.customFonts, true, "", "Inherit Global Font");
    const Editable = EditableProp || EditableComponent;
    const navigate = useNavigate();
    const [openSettings, setOpenSettings] = useState(null);
    const [isFooterBgOpen, setIsFooterBgOpen] = useState(false);
    const [activeFooterTab, setActiveFooterTab] = useState('background');
    const [columnActiveTab, setColumnActiveTab] = useState('type');
    const [mobileExpandedCols, setMobileExpandedCols] = useState({});

    const toggleMobileCol = (fIndex) => {
        setMobileExpandedCols(prev => ({
            ...prev,
            [fIndex]: !prev[fIndex]
        }));
    };

    const update = (path, value) => {
        if (typeof onUpdate === 'function') {
            onUpdate(path, value);
        } else if (typeof onSetConfig === 'function') {
            onSetConfig(prev => {
                const next = JSON.parse(JSON.stringify(prev || {}));
                let curr = next;
                for (let i = 0; i < path.length - 1; i++) {
                    const key = path[i];
                    if (curr[key] === undefined || curr[key] === null) {
                        curr[key] = typeof path[i + 1] === 'number' ? [] : {};
                    } else if (Array.isArray(curr[key])) {
                        curr[key] = [...curr[key]];
                    } else {
                        curr[key] = { ...curr[key] };
                    }
                    curr = curr[key];
                }
                curr[path[path.length - 1]] = value;
                return next;
            });
        } else if (typeof onSaveConfig === 'function') {
            const next = JSON.parse(JSON.stringify(config || {}));
            let curr = next;
            for (let i = 0; i < path.length - 1; i++) {
                const key = path[i];
                if (curr[key] === undefined || curr[key] === null) {
                    curr[key] = typeof path[i + 1] === 'number' ? [] : {};
                } else if (Array.isArray(curr[key])) {
                    curr[key] = [...curr[key]];
                } else {
                    curr[key] = { ...curr[key] };
                }
                curr = curr[key];
            }
            curr[path[path.length - 1]] = value;
            onSaveConfig(next);
        }
    };

    const handleHeadingStyleChange = (fIndex, p, v) => {
        update(['footer', fIndex, `heading${p}`], v);
        if (p === 'Weight') update(['footer', fIndex, 'headingFontWeight'], v);
        if (p === 'FontWeight') update(['footer', fIndex, 'headingWeight'], v);
        if (p === 'Size') update(['footer', fIndex, 'headingFontSize'], v);
        if (p === 'FontSize') update(['footer', fIndex, 'headingSize'], v);
        if (p === 'Transform') update(['footer', fIndex, 'headingTextTransform'], v);
        if (p === 'TextTransform') update(['footer', fIndex, 'headingTransform'], v);
    };

    const handleTextStyleChange = (fIndex, p, v) => {
        update(['footer', fIndex, `text${p}`], v);
        if (p === 'Weight') update(['footer', fIndex, 'textFontWeight'], v);
        if (p === 'FontWeight') update(['footer', fIndex, 'textWeight'], v);
        if (p === 'Size') update(['footer', fIndex, 'textFontSize'], v);
        if (p === 'FontSize') update(['footer', fIndex, 'textSize'], v);
        if (p === 'Transform') update(['footer', fIndex, 'textTextTransform'], v);
        if (p === 'TextTransform') update(['footer', fIndex, 'textTransform'], v);
    };

    const toggleSettings = (idx) => {
        setOpenSettings(prev => (prev === idx ? null : idx));
        setColumnActiveTab('type');
    };

    const renderIcon = (platform, size) => {
        if (getSocialIcon) return getSocialIcon(platform, size);
        return defaultGetSocialIcon(platform, size);
    };

    const layout = settings.footerLayout || 'columns';
    
    // Background computation
    const rawFooterBgImage = settings.footerBgImage || settings.footerBackgroundImage || '';
    const cleanFooterBgImage = typeof rawFooterBgImage === 'string' ? rawFooterBgImage.replace(/^url\(['"]?/, '').replace(/['"]?\)$/, '').trim() : '';
    const resolvedFooterBgUrl = cleanFooterBgImage ? resolveMediaUrl(cleanFooterBgImage) : '';
    const isFooterVideo = isVideo(resolvedFooterBgUrl);

    const footerBgImageStyle = (resolvedFooterBgUrl && !isFooterVideo)
        ? {
            backgroundImage: settings.footerBgGradient
                ? `${settings.footerBgGradient}, url("${resolvedFooterBgUrl}")`
                : `url("${resolvedFooterBgUrl}")`,
            backgroundSize: settings.footerBgSize || 'cover',
            backgroundPosition: settings.footerBgPosition || 'center',
            backgroundRepeat: settings.footerBgRepeat || 'no-repeat',
            backgroundAttachment: settings.footerBgAttachment || (settings.footerBgParallax ? 'fixed' : 'scroll'),
        }
        : (settings.footerBgGradient ? {
            backgroundImage: settings.footerBgGradient
        } : {});

    // 4-side padding resolution
    const footerPaddingTop = settings.footerPaddingTop !== undefined ? addUnit(settings.footerPaddingTop) : (settings.footerPadding ? addUnit(settings.footerPadding.split(' ')[0] || '40px') : '40px');
    const footerPaddingBottom = settings.footerPaddingBottom !== undefined ? addUnit(settings.footerPaddingBottom) : (settings.footerPadding ? addUnit(settings.footerPadding.split(' ')[2] || settings.footerPadding.split(' ')[0] || '40px') : '40px');
    const footerPaddingLeft = settings.footerPaddingLeft !== undefined ? addUnit(settings.footerPaddingLeft) : (settings.footerPadding ? addUnit(settings.footerPadding.split(' ')[3] || settings.footerPadding.split(' ')[1] || '0px') : '0px');
    const footerPaddingRight = settings.footerPaddingRight !== undefined ? addUnit(settings.footerPaddingRight) : (settings.footerPadding ? addUnit(settings.footerPadding.split(' ')[1] || '0px') : '0px');

    // Container layout resolution
    let containerStyle = {
        marginTop: 0,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: settings.footerAlignItems || 'flex-start',
        gap: addUnit(settings.footerColumnGap || '24px'),
        maxWidth: settings.footerMaxWidth ? addUnit(settings.footerMaxWidth) : undefined,
    };

    if (layout === 'centered') {
        containerStyle = {
            ...containerStyle,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: addUnit(settings.footerColumnGap || '28px'),
        };
    } else if (layout === 'split-brand') {
        containerStyle = {
            ...containerStyle,
            flexWrap: 'wrap',
            alignItems: 'stretch',
            gap: addUnit(settings.footerColumnGap || '32px'),
        };
    } else if (layout === 'bar-stacked' || layout === 'rows') {
        containerStyle = {
            ...containerStyle,
            flexDirection: 'column',
            gap: addUnit(settings.footerColumnGap || '24px'),
            width: '100%',
        };
    } else if (layout === 'rows-grid') {
        containerStyle = {
            marginTop: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: addUnit(settings.footerColumnGap || '28px'),
            alignItems: 'start',
            maxWidth: settings.footerMaxWidth ? addUnit(settings.footerMaxWidth) : undefined,
        };
    } else if (layout === '2-rows-split') {
        containerStyle = {
            ...containerStyle,
            flexWrap: 'wrap',
            gap: addUnit(settings.footerColumnGap || '28px'),
            alignItems: 'stretch',
        };
    } else if (layout === '3-rows-stacked') {
        containerStyle = {
            ...containerStyle,
            flexWrap: 'wrap',
            gap: addUnit(settings.footerColumnGap || '24px'),
            alignItems: 'stretch',
        };
    } else if (layout === 'inline-row') {
        containerStyle = {
            ...containerStyle,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: addUnit(settings.footerColumnGap || '20px'),
        };
    } else if (layout === 'card-grid') {
        containerStyle = {
            marginTop: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: addUnit(settings.footerColumnGap || '24px'),
            alignItems: 'stretch',
            maxWidth: settings.footerMaxWidth ? addUnit(settings.footerMaxWidth) : undefined,
        };
    } else if (layout === 'compact-bar') {
        containerStyle = {
            ...containerStyle,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: addUnit(settings.footerColumnGap || '20px'),
        };
    }

    const currentYear = new Date().getFullYear();
    const defaultCopyright = `© ${currentYear} ${stripHtml(settings.siteTitle) || 'OMNIVASO'}. All rights reserved.`;
    const copyrightText = settings.footerCopyrightText !== undefined ? settings.footerCopyrightText : defaultCopyright;

    // Border top calculation
    const borderTopValue = getSideBorder
        ? getSideBorder('footerBorderTop')
        : (settings.footerBorderTopWidth && settings.footerBorderTopStyle !== 'none'
            ? `${addUnit(settings.footerBorderTopWidth)} ${settings.footerBorderTopStyle || 'solid'} ${settings.footerBorderTopColor || '#333'}`
            : (settings.footerBorderTopWidth === '0px' || settings.footerBorderTopStyle === 'none' ? 'none' : '1px solid #333'));

    const borderBottomValue = (settings.footerBorderBottomWidth && settings.footerBorderBottomStyle !== 'none')
        ? `${addUnit(settings.footerBorderBottomWidth)} ${settings.footerBorderBottomStyle || 'solid'} ${settings.footerBorderBottomColor || '#333'}`
        : 'none';

    return (
        <>
        <footer
            id="footer"
            className={`footer-layout-${layout}`}
            style={{
                backgroundColor: settings.footerBgColor || '#1a1a1a',
                ...footerBgImageStyle,
                color: settings.footerTextColor || '#ffffff',
                paddingTop: footerPaddingTop,
                paddingBottom: footerPaddingBottom,
                paddingLeft: footerPaddingLeft,
                paddingRight: footerPaddingRight,
                marginTop: settings.footerMarginTop ? addUnit(settings.footerMarginTop) : undefined,
                marginBottom: settings.footerMarginBottom ? addUnit(settings.footerMarginBottom) : undefined,
                borderRadius: settings.footerBorderRadius ? addUnit(settings.footerBorderRadius) : undefined,
                borderTop: borderTopValue,
                borderBottom: borderBottomValue,
                fontFamily: settings.footerBodyFontFamily || 'inherit',
                fontSize: settings.footerBodyFontSize ? addUnit(settings.footerBodyFontSize) : undefined,
                lineHeight: settings.footerBodyLineHeight || '1.6',
                position: 'relative',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            {/* Background Video Layer if configured */}
            {isFooterVideo && (
                <video
                    autoPlay
                    loop={settings.footerBgLoop !== false}
                    muted
                    playsInline
                    src={resolvedFooterBgUrl}
                    style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: settings.footerBgSize === 'contain' ? 'contain' : 'cover',
                        objectPosition: settings.footerBgPosition || 'center',
                        zIndex: 0,
                        pointerEvents: 'none',
                        borderRadius: settings.footerBorderRadius ? addUnit(settings.footerBorderRadius) : undefined,
                    }}
                />
            )}

            {/* Background Overlay if configured */}
            {(settings.footerBgOverlay || settings.footerOverlayColor || settings.footerBgOverlayOpacity !== undefined) && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: settings.footerBgOverlay || settings.footerOverlayColor || '#000000',
                    opacity: settings.footerBgOverlayOpacity !== undefined ? settings.footerBgOverlayOpacity : (settings.footerBgOverlay ? 0.7 : 0),
                    pointerEvents: 'none',
                    zIndex: 0,
                    borderRadius: settings.footerBorderRadius ? addUnit(settings.footerBorderRadius) : undefined,
                }} />
            )}

            {/* Custom CSS Injection & Global Footer Overrides */}
            <style>{`
                #footer a {
                    color: ${settings.footerLinkColor || settings.globalLinkColor || settings.primaryColor || '#93c5fd'};
                    transition: all 0.2s ease;
                }
                #footer a:hover {
                    color: ${settings.footerLinkHoverColor || settings.globalLinkHoverColor || '#60a5fa'};
                }
                #footer h1, #footer h2, #footer h3, #footer h4, #footer h5, #footer h6 {
                    font-family: ${settings.footerHeadingFontFamily || 'inherit'};
                    ${settings.footerHeadingFontWeight ? `font-weight: ${settings.footerHeadingFontWeight};` : ''}
                    ${settings.footerHeadingTextTransform ? `text-transform: ${settings.footerHeadingTextTransform};` : ''}
                    ${settings.footerHeadingLetterSpacing ? `letter-spacing: ${addUnit(settings.footerHeadingLetterSpacing)};` : ''}
                    ${settings.footerHeadingColor ? `color: ${settings.footerHeadingColor};` : ''}
                }
                @media (max-width: 768px) {
                    ${settings.hideFooterOnMobile ? `
                    #footer {
                        display: none !important;
                    }
                    ` : `
                    #footer {
                        ${settings.mobileFooterPaddingTop !== undefined && settings.mobileFooterPaddingTop !== '' ? `padding-top: ${addUnit(settings.mobileFooterPaddingTop)} !important;` : (settings.mobileFooterPaddingTopBottom ? `padding-top: ${addUnit(settings.mobileFooterPaddingTopBottom)} !important;` : '')}
                        ${settings.mobileFooterPaddingBottom !== undefined && settings.mobileFooterPaddingBottom !== '' ? `padding-bottom: ${addUnit(settings.mobileFooterPaddingBottom)} !important;` : (settings.mobileFooterPaddingTopBottom ? `padding-bottom: ${addUnit(settings.mobileFooterPaddingTopBottom)} !important;` : '')}
                        ${settings.mobileFooterPaddingLeft !== undefined && settings.mobileFooterPaddingLeft !== '' ? `padding-left: ${addUnit(settings.mobileFooterPaddingLeft)} !important;` : (settings.mobileFooterPaddingLeftRight ? `padding-left: ${addUnit(settings.mobileFooterPaddingLeftRight)} !important;` : '')}
                        ${settings.mobileFooterPaddingRight !== undefined && settings.mobileFooterPaddingRight !== '' ? `padding-right: ${addUnit(settings.mobileFooterPaddingRight)} !important;` : (settings.mobileFooterPaddingLeftRight ? `padding-right: ${addUnit(settings.mobileFooterPaddingLeftRight)} !important;` : '')}
                        ${settings.mobileFooterTextAlign && settings.mobileFooterTextAlign !== 'inherit' ? `text-align: ${settings.mobileFooterTextAlign} !important;` : ''}
                        ${settings.mobileFooterBodyFontSize ? `font-size: ${addUnit(settings.mobileFooterBodyFontSize)} !important;` : ''}
                        ${settings.mobileFooterLineHeight ? `line-height: ${settings.mobileFooterLineHeight} !important;` : ''}
                    }
                    #footer .container {
                        ${settings.mobileFooterColumnGap ? `gap: ${addUnit(settings.mobileFooterColumnGap)} !important;` : ''}
                        ${(settings.mobileFooterTextAlign === 'center' || settings.mobileFooterLayout === 'compact-center') ? 'justify-content: center !important;' : ''}
                    }
                    #footer .footer-column {
                        ${settings.mobileFooterLayout === 'grid-2'
                            ? 'flex: 1 1 calc(50% - 12px) !important; max-width: calc(50% - 12px) !important; min-width: 130px !important;'
                            : (settings.mobileFooterLayout === 'compact-center'
                                ? 'flex: 1 1 100% !important; max-width: 100% !important; text-align: center !important;'
                                : 'flex: 1 1 100% !important; max-width: 100% !important;')}
                        border-right: none !important;
                        padding-right: 0 !important;
                        margin-bottom: ${addUnit(settings.mobileFooterColumnMarginBottom || '16px')} !important;
                        ${settings.mobileFooterTextAlign && settings.mobileFooterTextAlign !== 'inherit' ? `text-align: ${settings.mobileFooterTextAlign} !important;` : ''}
                        ${settings.mobileFooterColumnDivider && settings.mobileFooterColumnDivider !== 'none' ? `border-bottom: ${settings.mobileFooterColumnDivider} !important; padding-bottom: ${addUnit(settings.mobileFooterColumnMarginBottom || '16px')} !important;` : ''}
                    }
                    #footer .footer-column.footer-col-hide-mobile {
                        display: none !important;
                    }
                    #footer h1, #footer h2, #footer h3, #footer h4, #footer h5, #footer h6 {
                        ${settings.mobileFooterHeadingFontSize ? `font-size: ${addUnit(settings.mobileFooterHeadingFontSize)} !important;` : ''}
                        ${settings.mobileFooterHeadingAlign && settings.mobileFooterHeadingAlign !== 'inherit' ? `text-align: ${settings.mobileFooterHeadingAlign} !important;` : (settings.mobileFooterTextAlign && settings.mobileFooterTextAlign !== 'inherit' ? `text-align: ${settings.mobileFooterTextAlign} !important;` : '')}
                    }
                    #footer .footer-socials {
                        ${settings.mobileFooterSocialsAlign && settings.mobileFooterSocialsAlign !== 'inherit' ? `justify-content: ${settings.mobileFooterSocialsAlign === 'center' ? 'center' : (settings.mobileFooterSocialsAlign === 'left' ? 'flex-start' : (settings.mobileFooterSocialsAlign === 'right' ? 'flex-end' : settings.mobileFooterSocialsAlign))} !important;` : (settings.mobileFooterTextAlign === 'center' ? 'justify-content: center !important;' : '')}
                        ${settings.mobileFooterSocialsGap ? `gap: ${addUnit(settings.mobileFooterSocialsGap)} !important;` : ''}
                    }
                    #footer .footer-socials a svg {
                        ${settings.mobileFooterSocialsSize ? `width: ${addUnit(settings.mobileFooterSocialsSize)} !important; height: ${addUnit(settings.mobileFooterSocialsSize)} !important;` : ''}
                    }
                    #footer .footer-bottom-bar {
                        ${settings.mobileFooterBottomBarDirection ? `flex-direction: ${settings.mobileFooterBottomBarDirection} !important;` : 'flex-direction: column !important;'}
                        ${settings.mobileFooterBottomBarAlign ? `align-items: ${settings.mobileFooterBottomBarAlign} !important;` : 'align-items: center !important;'}
                        ${settings.mobileFooterBottomBarPadding ? `padding: ${addUnit(settings.mobileFooterBottomBarPadding)} 0 !important;` : ''}
                        ${settings.mobileFooterTextAlign && settings.mobileFooterTextAlign !== 'inherit' ? `text-align: ${settings.mobileFooterTextAlign} !important;` : 'text-align: center !important;'}
                    }
                    #footer .footer-copyright, #footer .footer-bottom-bar span, #footer .footer-bottom-bar a {
                        ${settings.mobileFooterCopyrightFontSize ? `font-size: ${addUnit(settings.mobileFooterCopyrightFontSize)} !important;` : ''}
                    }
                    .footer-accordion-header {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: space-between !important;
                        cursor: pointer !important;
                        user-select: none !important;
                        padding: 4px 0 !important;
                    }
                    .footer-accordion-indicator {
                        display: inline-flex !important;
                        font-size: 11px !important;
                        opacity: 0.75 !important;
                    }
                    .footer-accordion-body-closed {
                        display: none !important;
                    }
                    .footer-accordion-body-open {
                        display: block !important;
                        animation: footerAccordionFade 0.2s ease !important;
                    }
                    `}
                    ${(footer || []).map((col, idx) => {
                        const rules = [];
                        if (col.mobileWidth) rules.push(`flex: 0 0 ${col.mobileWidth} !important; max-width: ${col.mobileWidth} !important;`);
                        if (col.mobileOrder !== undefined && col.mobileOrder !== '') rules.push(`order: ${col.mobileOrder} !important;`);
                        if (col.mobileTextAlign && col.mobileTextAlign !== 'inherit') rules.push(`text-align: ${col.mobileTextAlign} !important;`);
                        if (col.mobileMarginBottom) rules.push(`margin-bottom: ${addUnit(col.mobileMarginBottom)} !important;`);
                        return rules.length > 0 ? `#footer .footer-column.col-${idx} { ${rules.join(' ')} }` : '';
                    }).filter(Boolean).join('\n')}
                    ${settings.mobileFooterCustomCss || ''}
                }
                @media (min-width: 769px) {
                    .footer-accordion-indicator {
                        display: none !important;
                    }
                    .footer-accordion-body-closed {
                        display: block !important;
                    }
                    .footer-accordion-body-open {
                        display: block !important;
                    }
                }
                @keyframes footerAccordionFade {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                ${settings.footerCustomCss || ''}
            `}</style>

            {/* ── Edit Footer Background Floating Button & Panel ── */}
            {isEditing && (
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto 16px auto',
                    padding: '0 20px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    position: 'relative',
                    zIndex: 10,
                }}>
                    <button
                        onClick={() => setIsFooterBgOpen(!isFooterBgOpen)}
                        style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <LuPalette size={13} /> Edit Footer Styling & Layout
                    </button>
                </div>
            )}

            {isEditing && isFooterBgOpen && (
                <DraggableToolbar title="Footer Styling & Full CSS Controls" onClose={() => setIsFooterBgOpen(false)}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '320px',
                        maxWidth: '92vw',
                        maxHeight: '75vh',
                        background: '#ffffff',
                        color: '#1e293b',
                        boxSizing: 'border-box',
                    }} onClick={e => e.stopPropagation()}>
                        
                        {/* Tab Bar Header */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '2px',
                            background: '#f1f5f9',
                            padding: '6px 6px 0 6px',
                            borderBottom: '1px solid #cbd5e1'
                        }}>
                            {[
                                { id: 'background', label: '🎨 Background' },
                                { id: 'layout', label: '📐 Spacing' },
                                { id: 'typography', label: '🔤 Typography' },
                                { id: 'borders', label: '🖼️ Borders' },
                                { id: 'bottombar', label: '📜 Bottom Bar' },
                                { id: 'mobile', label: '📱 Mobile' },
                                { id: 'customcss', label: '💻 CSS' },
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveFooterTab(tab.id)}
                                    style={tabBtnStyle(activeFooterTab === tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Tab Contents Container */}
                        <div style={{
                            padding: '12px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            fontSize: '11px'
                        }}>
                            {/* TAB 1: BACKGROUND & COLORS */}
                            {activeFooterTab === 'background' && (
                                <>
                                    <div>
                                        <label style={settingLabel}>Footer Background Color</label>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <input
                                                type="color"
                                                value={toValidHex(settings.footerBgColor || '#1a1a1a')}
                                                onChange={e => update(['settings', 'footerBgColor'], e.target.value)}
                                                style={{ width: '32px', height: '28px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}
                                            />
                                            <input
                                                type="text"
                                                value={settings.footerBgColor || ''}
                                                onChange={e => update(['settings', 'footerBgColor'], e.target.value)}
                                                placeholder="#1a1a1a or rgba(...)"
                                                style={{ ...settingInput, flex: 1 }}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label style={settingLabel}>Background Gradient (CSS override)</label>
                                        <input
                                            type="text"
                                            value={settings.footerBgGradient || ''}
                                            onChange={e => update(['settings', 'footerBgGradient'], e.target.value)}
                                            placeholder="linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)"
                                            style={settingInput}
                                        />
                                        <span style={{ fontSize: '9px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                                            Leave empty to use solid background color above.
                                        </span>
                                    </div>

                                    <div>
                                        <label style={settingLabel}>Background Image</label>
                                        <MediaInput
                                            size="small"
                                            value={settings.footerBgImage || settings.footerBackgroundImage || ''}
                                            onChange={e => {
                                                const val = e?.target ? e.target.value : e;
                                                update(['settings', 'footerBgImage'], val);
                                                update(['settings', 'footerBackgroundImage'], val);
                                            }}
                                            placeholder="Upload or paste background image URL"
                                        />
                                    </div>

                                    {(settings.footerBgImage || settings.footerBackgroundImage) && (
                                        <>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                <div>
                                                    <label style={settingLabel}>Image Size</label>
                                                    <select
                                                        value={settings.footerBgSize || 'cover'}
                                                        onChange={e => update(['settings', 'footerBgSize'], e.target.value)}
                                                        style={settingInput}
                                                    >
                                                        <option value="cover">Cover (Full Area)</option>
                                                        <option value="contain">Contain (Fit)</option>
                                                        <option value="auto">Auto (Original)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={settingLabel}>Image Position</label>
                                                    <select
                                                        value={settings.footerBgPosition || 'center'}
                                                        onChange={e => update(['settings', 'footerBgPosition'], e.target.value)}
                                                        style={settingInput}
                                                    >
                                                        <option value="center">Center</option>
                                                        <option value="top">Top</option>
                                                        <option value="bottom">Bottom</option>
                                                        <option value="left">Left</option>
                                                        <option value="right">Right</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                <div>
                                                    <label style={settingLabel}>Image Repeat</label>
                                                    <select
                                                        value={settings.footerBgRepeat || 'no-repeat'}
                                                        onChange={e => update(['settings', 'footerBgRepeat'], e.target.value)}
                                                        style={settingInput}
                                                    >
                                                        <option value="no-repeat">No Repeat</option>
                                                        <option value="repeat">Repeat (Tile)</option>
                                                        <option value="repeat-x">Repeat Horizontally</option>
                                                        <option value="repeat-y">Repeat Vertically</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={settingLabel}>Parallax / Attachment</label>
                                                    <select
                                                        value={settings.footerBgAttachment || (settings.footerBgParallax ? 'fixed' : 'scroll')}
                                                        onChange={e => {
                                                            update(['settings', 'footerBgAttachment'], e.target.value);
                                                            update(['settings', 'footerBgParallax'], e.target.value === 'fixed');
                                                        }}
                                                        style={settingInput}
                                                    >
                                                        <option value="scroll">Scroll (Standard)</option>
                                                        <option value="fixed">Fixed (Parallax Effect)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div>
                                        <label style={settingLabel}>Dark / Colored Overlay</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            <input
                                                type="text"
                                                value={settings.footerBgOverlay || ''}
                                                onChange={e => update(['settings', 'footerBgOverlay'], e.target.value)}
                                                placeholder="rgba(0,0,0,0.6)"
                                                style={settingInput}
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                value={settings.footerBgOverlayOpacity !== undefined ? settings.footerBgOverlayOpacity : 0.7}
                                                onChange={e => update(['settings', 'footerBgOverlayOpacity'], parseFloat(e.target.value))}
                                                placeholder="Opacity 0.7"
                                                style={settingInput}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>General Text & Link Colors</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Body Text Color</label>
                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    <input
                                                        type="color"
                                                        value={toValidHex(settings.footerTextColor || '#ffffff')}
                                                        onChange={e => update(['settings', 'footerTextColor'], e.target.value)}
                                                        style={{ width: '28px', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={settings.footerTextColor || ''}
                                                        onChange={e => update(['settings', 'footerTextColor'], e.target.value)}
                                                        placeholder="#ffffff"
                                                        style={{ ...settingInput, flex: 1 }}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Heading Color</label>
                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    <input
                                                        type="color"
                                                        value={toValidHex(settings.footerHeadingColor || '#ffffff')}
                                                        onChange={e => update(['settings', 'footerHeadingColor'], e.target.value)}
                                                        style={{ width: '28px', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={settings.footerHeadingColor || ''}
                                                        onChange={e => update(['settings', 'footerHeadingColor'], e.target.value)}
                                                        placeholder="Inherit"
                                                        style={{ ...settingInput, flex: 1 }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Link Color</label>
                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    <input
                                                        type="color"
                                                        value={toValidHex(settings.footerLinkColor || '#93c5fd')}
                                                        onChange={e => update(['settings', 'footerLinkColor'], e.target.value)}
                                                        style={{ width: '28px', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={settings.footerLinkColor || ''}
                                                        onChange={e => update(['settings', 'footerLinkColor'], e.target.value)}
                                                        placeholder="#93c5fd"
                                                        style={{ ...settingInput, flex: 1 }}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Link Hover Color</label>
                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    <input
                                                        type="color"
                                                        value={toValidHex(settings.footerLinkHoverColor || '#60a5fa')}
                                                        onChange={e => update(['settings', 'footerLinkHoverColor'], e.target.value)}
                                                        style={{ width: '28px', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={settings.footerLinkHoverColor || ''}
                                                        onChange={e => update(['settings', 'footerLinkHoverColor'], e.target.value)}
                                                        placeholder="#60a5fa"
                                                        style={{ ...settingInput, flex: 1 }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* TAB 2: SPACING & LAYOUT */}
                            {activeFooterTab === 'layout' && (
                                <>
                                    <div>
                                        <label style={settingLabel}>Layout Preset</label>
                                        <select
                                            value={settings.footerLayout || 'columns'}
                                            onChange={e => update(['settings', 'footerLayout'], e.target.value)}
                                            style={settingInput}
                                        >
                                            <option value="columns">Multi-Columns (Standard Flex)</option>
                                            <option value="centered">Centered Single Column</option>
                                            <option value="split-brand">Split Brand Column + Links</option>
                                            <option value="bar-stacked">Stacked Full-Width Rows</option>
                                            <option value="card-grid">Modern Card Grid</option>
                                            <option value="rows-grid">Adaptive Auto-Fit Grid</option>
                                            <option value="2-rows-split">2-Rows (Header Row + Columns)</option>
                                            <option value="3-rows-stacked">3-Rows (Top, Middle, Bottom)</option>
                                            <option value="inline-row">Single Inline Bar</option>
                                            <option value="compact-bar">Compact Navigation Bar</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label style={settingLabel}>Container Max Width</label>
                                        <input
                                            type="text"
                                            value={settings.footerMaxWidth || ''}
                                            onChange={e => update(['settings', 'footerMaxWidth'], e.target.value)}
                                            placeholder="1200px (or 100%, 1400px)"
                                            style={settingInput}
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                        <div>
                                            <label style={settingLabel}>Column Gap</label>
                                            <input
                                                type="text"
                                                value={settings.footerColumnGap || '24px'}
                                                onChange={e => update(['settings', 'footerColumnGap'], e.target.value)}
                                                placeholder="24px"
                                                style={settingInput}
                                            />
                                        </div>
                                        <div>
                                            <label style={settingLabel}>Column Alignment</label>
                                            <select
                                                value={settings.footerAlignItems || 'flex-start'}
                                                onChange={e => update(['settings', 'footerAlignItems'], e.target.value)}
                                                style={settingInput}
                                            >
                                                <option value="flex-start">Top Align</option>
                                                <option value="center">Center Align</option>
                                                <option value="flex-end">Bottom Align</option>
                                                <option value="stretch">Stretch Full Height</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>4-Side Footer Padding</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Top Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerPaddingTop !== undefined ? settings.footerPaddingTop : '40px'}
                                                    onChange={e => update(['settings', 'footerPaddingTop'], e.target.value)}
                                                    placeholder="40px"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Bottom Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerPaddingBottom !== undefined ? settings.footerPaddingBottom : '40px'}
                                                    onChange={e => update(['settings', 'footerPaddingBottom'], e.target.value)}
                                                    placeholder="40px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Left Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerPaddingLeft !== undefined ? settings.footerPaddingLeft : '0px'}
                                                    onChange={e => update(['settings', 'footerPaddingLeft'], e.target.value)}
                                                    placeholder="0px"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Right Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerPaddingRight !== undefined ? settings.footerPaddingRight : '0px'}
                                                    onChange={e => update(['settings', 'footerPaddingRight'], e.target.value)}
                                                    placeholder="0px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Footer Margins</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Margin Top</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerMarginTop || ''}
                                                    onChange={e => update(['settings', 'footerMarginTop'], e.target.value)}
                                                    placeholder="0px (e.g. 40px)"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Margin Bottom</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerMarginBottom || ''}
                                                    onChange={e => update(['settings', 'footerMarginBottom'], e.target.value)}
                                                    placeholder="0px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>📱 Mobile Responsive Spacing</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Mobile Top/Bottom</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterPaddingTopBottom || ''}
                                                    onChange={e => update(['settings', 'mobileFooterPaddingTopBottom'], e.target.value)}
                                                    placeholder="24px"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Mobile Left/Right</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterPaddingLeftRight || ''}
                                                    onChange={e => update(['settings', 'mobileFooterPaddingLeftRight'], e.target.value)}
                                                    placeholder="16px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* TAB 3: TYPOGRAPHY */}
                            {activeFooterTab === 'typography' && (
                                <>
                                    <div>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Footer Headings (h4)</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Font Family</label>
                                                <select
                                                    value={settings.footerHeadingFontFamily || ''}
                                                    onChange={e => update(['settings', 'footerHeadingFontFamily'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    {fontOptions.map((f, idx) => (
                                                        <option key={idx} value={f.value}>{f.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                <div>
                                                    <label style={settingLabel}>Font Weight</label>
                                                    <select
                                                        value={settings.footerHeadingFontWeight || '600'}
                                                        onChange={e => update(['settings', 'footerHeadingFontWeight'], e.target.value)}
                                                        style={settingInput}
                                                    >
                                                        <option value="normal">Normal (400)</option>
                                                        <option value="500">Medium (500)</option>
                                                        <option value="600">Semi-Bold (600)</option>
                                                        <option value="700">Bold (700)</option>
                                                        <option value="800">Extra Bold (800)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={settingLabel}>Text Transform</label>
                                                    <select
                                                        value={settings.footerHeadingTextTransform || 'none'}
                                                        onChange={e => update(['settings', 'footerHeadingTextTransform'], e.target.value)}
                                                        style={settingInput}
                                                    >
                                                        <option value="none">None</option>
                                                        <option value="uppercase">UPPERCASE</option>
                                                        <option value="capitalize">Capitalize</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Letter Spacing</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerHeadingLetterSpacing || ''}
                                                    onChange={e => update(['settings', 'footerHeadingLetterSpacing'], e.target.value)}
                                                    placeholder="normal or 0.05em"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Footer Body & Links Typography</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Body Font Family</label>
                                                <select
                                                    value={settings.footerBodyFontFamily || ''}
                                                    onChange={e => update(['settings', 'footerBodyFontFamily'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    {fontOptions.map((f, idx) => (
                                                        <option key={idx} value={f.value}>{f.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                <div>
                                                    <label style={settingLabel}>Body Font Size</label>
                                                    <input
                                                        type="text"
                                                        value={settings.footerBodyFontSize || ''}
                                                        onChange={e => update(['settings', 'footerBodyFontSize'], e.target.value)}
                                                        placeholder="0.9rem or 14px"
                                                        style={settingInput}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={settingLabel}>Line Height</label>
                                                    <input
                                                        type="text"
                                                        value={settings.footerBodyLineHeight || '1.6'}
                                                        onChange={e => update(['settings', 'footerBodyLineHeight'], e.target.value)}
                                                        placeholder="1.6"
                                                        style={settingInput}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* TAB 4: BORDERS & DIVIDERS */}
                            {activeFooterTab === 'borders' && (
                                <>
                                    <div>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Footer Top Border</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Border Width</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerBorderTopWidth !== undefined ? settings.footerBorderTopWidth : '1px'}
                                                    onChange={e => update(['settings', 'footerBorderTopWidth'], e.target.value)}
                                                    placeholder="1px (or 0px)"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Border Style</label>
                                                <select
                                                    value={settings.footerBorderTopStyle || 'solid'}
                                                    onChange={e => update(['settings', 'footerBorderTopStyle'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    <option value="solid">Solid</option>
                                                    <option value="dashed">Dashed</option>
                                                    <option value="dotted">Dotted</option>
                                                    <option value="none">None</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label style={settingLabel}>Border Color</label>
                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                <input
                                                    type="color"
                                                    value={toValidHex(settings.footerBorderTopColor || '#333333')}
                                                    onChange={e => update(['settings', 'footerBorderTopColor'], e.target.value)}
                                                    style={{ width: '32px', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                />
                                                <input
                                                    type="text"
                                                    value={settings.footerBorderTopColor || ''}
                                                    onChange={e => update(['settings', 'footerBorderTopColor'], e.target.value)}
                                                    placeholder="#333333"
                                                    style={{ ...settingInput, flex: 1 }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Footer Bottom Border & Radius</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Bottom Border Width</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerBorderBottomWidth || ''}
                                                    onChange={e => update(['settings', 'footerBorderBottomWidth'], e.target.value)}
                                                    placeholder="0px"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Border Radius</label>
                                                <input
                                                    type="text"
                                                    value={settings.footerBorderRadius || ''}
                                                    onChange={e => update(['settings', 'footerBorderRadius'], e.target.value)}
                                                    placeholder="0px (e.g. 16px)"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>Horizontal Dividing Line</span>
                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '9px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!settings.showFooterHorizontalDivider}
                                                    onChange={e => update(['settings', 'showFooterHorizontalDivider'], e.target.checked)}
                                                />
                                                Show Line
                                            </label>
                                        </label>
                                        {settings.showFooterHorizontalDivider && (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '6px' }}>
                                                <div>
                                                    <label style={settingLabel}>Divider Width</label>
                                                    <input
                                                        type="text"
                                                        value={settings.footerHorizontalDividerWidth || '1px'}
                                                        onChange={e => update(['settings', 'footerHorizontalDividerWidth'], e.target.value)}
                                                        placeholder="1px"
                                                        style={settingInput}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={settingLabel}>Divider Margin</label>
                                                    <input
                                                        type="text"
                                                        value={settings.footerHorizontalDividerMargin || '24px 0'}
                                                        onChange={e => update(['settings', 'footerHorizontalDividerMargin'], e.target.value)}
                                                        placeholder="24px 0"
                                                        style={settingInput}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* TAB 5: BOTTOM BAR & COPYRIGHT */}
                            {activeFooterTab === 'bottombar' && (
                                <>
                                    <div>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Copyright Text</label>
                                        <input
                                            type="text"
                                            value={settings.footerCopyrightText !== undefined ? settings.footerCopyrightText : defaultCopyright}
                                            onChange={e => update(['settings', 'footerCopyrightText'], e.target.value)}
                                            placeholder={defaultCopyright}
                                            style={settingInput}
                                        />
                                        <span style={{ fontSize: '9px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                                            Tip: You can also edit copyright directly inline in the footer.
                                        </span>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                        <div>
                                            <label style={settingLabel}>Copyright Color</label>
                                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                <input
                                                    type="color"
                                                    value={toValidHex(settings.footerCopyrightColor || '#94a3b8')}
                                                    onChange={e => update(['settings', 'footerCopyrightColor'], e.target.value)}
                                                    style={{ width: '28px', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                />
                                                <input
                                                    type="text"
                                                    value={settings.footerCopyrightColor || ''}
                                                    onChange={e => update(['settings', 'footerCopyrightColor'], e.target.value)}
                                                    placeholder="#94a3b8"
                                                    style={{ ...settingInput, flex: 1 }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label style={settingLabel}>Copyright Font Size</label>
                                            <input
                                                type="text"
                                                value={settings.footerCopyrightFontSize || '0.8rem'}
                                                onChange={e => update(['settings', 'footerCopyrightFontSize'], e.target.value)}
                                                placeholder="0.8rem"
                                                style={settingInput}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, margin: 0 }}>Show Bottom Bar Social Icons</label>
                                        <input
                                            type="checkbox"
                                            checked={settings.showFooterBottomSocials !== false}
                                            onChange={e => update(['settings', 'showFooterBottomSocials'], e.target.checked)}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <label style={{ ...settingLabel, margin: 0 }}>Show Admin Login Link</label>
                                        <input
                                            type="checkbox"
                                            checked={settings.hideFooterAdminLogin !== true}
                                            onChange={e => update(['settings', 'hideFooterAdminLogin'], !e.target.checked)}
                                        />
                                    </div>

                                    {/* Return to Top Scroll Button */}
                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>⬆️ 'Return to Top' Button</span>
                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '9px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={settings.returnToTopEnable !== false && settings.returnToTopEnable !== 'false'}
                                                    onChange={e => update(['settings', 'returnToTopEnable'], e.target.checked)}
                                                />
                                                Enable
                                            </label>
                                        </label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Position</label>
                                                <select
                                                    value={settings.returnToTopPosition || 'bottom-right'}
                                                    onChange={e => update(['settings', 'returnToTopPosition'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    <option value="bottom-right">Bottom Right</option>
                                                    <option value="bottom-left">Bottom Left</option>
                                                    <option value="bottom-center">Bottom Center</option>
                                                    <option value="top-right">Top Right</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Shape</label>
                                                <select
                                                    value={settings.returnToTopShape || 'circle'}
                                                    onChange={e => update(['settings', 'returnToTopShape'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    <option value="circle">Circle</option>
                                                    <option value="rounded">Rounded</option>
                                                    <option value="square">Square</option>
                                                    <option value="pill">Pill (Icon+Text)</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={settingLabel}>Bg Color</label>
                                                <input
                                                    type="color"
                                                    value={toValidHex(settings.returnToTopBgColor || settings.primaryColor || '#3b82f6')}
                                                    onChange={e => update(['settings', 'returnToTopBgColor'], e.target.value)}
                                                    style={{ width: '100%', height: '24px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={settingLabel}>Icon Color</label>
                                                <input
                                                    type="color"
                                                    value={toValidHex(settings.returnToTopTextColor || '#ffffff')}
                                                    onChange={e => update(['settings', 'returnToTopTextColor'], e.target.value)}
                                                    style={{ width: '100%', height: '24px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* TAB 5: MOBILE CONFIGURATION */}
                            {activeFooterTab === 'mobile' && (
                                <>
                                    <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        <label style={{ ...settingLabel, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0, fontWeight: 'bold' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!settings.hideFooterOnMobile}
                                                onChange={e => update(['settings', 'hideFooterOnMobile'], e.target.checked)}
                                                style={{ width: '16px', height: '16px', accentColor: '#ef4444' }}
                                            />
                                            <span>📱 Hide Entire Footer on Mobile (≤ 768px)</span>
                                        </label>
                                        <p style={{ margin: '4px 0 0 24px', fontSize: '10px', color: '#64748b' }}>
                                            Hides the entire footer block on smartphones and small viewports.
                                        </p>
                                    </div>

                                    <div>
                                        <label style={settingLabel}>Mobile Layout Mode</label>
                                        <select
                                            value={settings.mobileFooterLayout || 'stack'}
                                            onChange={e => update(['settings', 'mobileFooterLayout'], e.target.value)}
                                            style={settingInput}
                                        >
                                            <option value="stack">1-Column Stack (Standard Vertical)</option>
                                            <option value="grid-2">2-Column Paired Grid (50% / 50%)</option>
                                            <option value="accordion">Collapsible Mobile Accordion (Tap to Expand)</option>
                                            <option value="compact-center">Minimalist Centered Stack</option>
                                        </select>
                                        <span style={{ fontSize: '9px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                                            {settings.mobileFooterLayout === 'accordion'
                                                ? '✨ Column headings become interactive accordions on mobile viewports.'
                                                : settings.mobileFooterLayout === 'grid-2'
                                                    ? '✨ Columns render side-by-side in 2-column rows on mobile screens.'
                                                    : '✨ Columns stack cleanly 1-by-1 on mobile screens.'}
                                        </span>
                                    </div>

                                    <div>
                                        <label style={settingLabel}>Mobile Text Alignment</label>
                                        <select
                                            value={settings.mobileFooterTextAlign || 'inherit'}
                                            onChange={e => update(['settings', 'mobileFooterTextAlign'], e.target.value)}
                                            style={settingInput}
                                        >
                                            <option value="inherit">Inherit Desktop Alignment</option>
                                            <option value="center">Center Align All Content</option>
                                            <option value="left">Left Align All Content</option>
                                            <option value="right">Right Align All Content</option>
                                        </select>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Mobile Padding & Spacing</label>
                                        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                                            {[
                                                { label: 'Compact', top: '16px', horiz: '12px' },
                                                { label: 'Standard', top: '24px', horiz: '16px' },
                                                { label: 'Spacious', top: '36px', horiz: '20px' },
                                            ].map(preset => (
                                                <button
                                                    key={preset.label}
                                                    type="button"
                                                    onClick={() => {
                                                        update(['settings', 'mobileFooterPaddingTopBottom'], preset.top);
                                                        update(['settings', 'mobileFooterPaddingLeftRight'], preset.horiz);
                                                        update(['settings', 'mobileFooterPaddingTop'], preset.top);
                                                        update(['settings', 'mobileFooterPaddingBottom'], preset.top);
                                                        update(['settings', 'mobileFooterPaddingLeft'], preset.horiz);
                                                        update(['settings', 'mobileFooterPaddingRight'], preset.horiz);
                                                    }}
                                                    style={{ ...ctrlBtn(), flex: 1, padding: '3px 4px', fontSize: '10px' }}
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Top Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterPaddingTop !== undefined ? settings.mobileFooterPaddingTop : (settings.mobileFooterPaddingTopBottom || '')}
                                                    onChange={e => update(['settings', 'mobileFooterPaddingTop'], e.target.value)}
                                                    placeholder="24px"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Bottom Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterPaddingBottom !== undefined ? settings.mobileFooterPaddingBottom : (settings.mobileFooterPaddingTopBottom || '')}
                                                    onChange={e => update(['settings', 'mobileFooterPaddingBottom'], e.target.value)}
                                                    placeholder="24px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Left Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterPaddingLeft !== undefined ? settings.mobileFooterPaddingLeft : (settings.mobileFooterPaddingLeftRight || '')}
                                                    onChange={e => update(['settings', 'mobileFooterPaddingLeft'], e.target.value)}
                                                    placeholder="16px"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Right Padding</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterPaddingRight !== undefined ? settings.mobileFooterPaddingRight : (settings.mobileFooterPaddingLeftRight || '')}
                                                    onChange={e => update(['settings', 'mobileFooterPaddingRight'], e.target.value)}
                                                    placeholder="16px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Mobile Column Spacing & Dividers</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Column Gap</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterColumnGap || ''}
                                                    onChange={e => update(['settings', 'mobileFooterColumnGap'], e.target.value)}
                                                    placeholder="20px"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Bottom Spacing</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterColumnMarginBottom || ''}
                                                    onChange={e => update(['settings', 'mobileFooterColumnMarginBottom'], e.target.value)}
                                                    placeholder="16px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label style={settingLabel}>Mobile Column Dividers</label>
                                            <select
                                                value={settings.mobileFooterColumnDivider || 'none'}
                                                onChange={e => update(['settings', 'mobileFooterColumnDivider'], e.target.value)}
                                                style={settingInput}
                                            >
                                                <option value="none">None (Clean Separated)</option>
                                                <option value="1px solid rgba(255,255,255,0.1)">Subtle Light Divider</option>
                                                <option value="1px solid rgba(0,0,0,0.1)">Subtle Dark Divider</option>
                                                <option value="1px dashed rgba(255,255,255,0.15)">Dashed Divider</option>
                                                <option value="1px solid #3b82f6">Accent Line Divider</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Mobile Typography Overrides</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Heading Font Size</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterHeadingFontSize || ''}
                                                    onChange={e => update(['settings', 'mobileFooterHeadingFontSize'], e.target.value)}
                                                    placeholder="e.g. 1rem (Auto)"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Heading Align</label>
                                                <select
                                                    value={settings.mobileFooterHeadingAlign || 'inherit'}
                                                    onChange={e => update(['settings', 'mobileFooterHeadingAlign'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    <option value="inherit">Inherit</option>
                                                    <option value="center">Center</option>
                                                    <option value="left">Left</option>
                                                    <option value="right">Right</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Body Font Size</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterBodyFontSize || ''}
                                                    onChange={e => update(['settings', 'mobileFooterBodyFontSize'], e.target.value)}
                                                    placeholder="e.g. 0.85rem (Auto)"
                                                    style={settingInput}
                                                />
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Line Height</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterLineHeight || ''}
                                                    onChange={e => update(['settings', 'mobileFooterLineHeight'], e.target.value)}
                                                    placeholder="e.g. 1.5"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={{ ...settingLabel, color: '#2563eb' }}>Mobile Social Icons & Bottom Bar</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Socials Align</label>
                                                <select
                                                    value={settings.mobileFooterSocialsAlign || 'inherit'}
                                                    onChange={e => update(['settings', 'mobileFooterSocialsAlign'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    <option value="inherit">Inherit</option>
                                                    <option value="center">Center</option>
                                                    <option value="left">Left</option>
                                                    <option value="right">Right</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Icon Size</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterSocialsSize || ''}
                                                    onChange={e => update(['settings', 'mobileFooterSocialsSize'], e.target.value)}
                                                    placeholder="20px"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            <div>
                                                <label style={settingLabel}>Bottom Bar Direction</label>
                                                <select
                                                    value={settings.mobileFooterBottomBarDirection || 'column'}
                                                    onChange={e => update(['settings', 'mobileFooterBottomBarDirection'], e.target.value)}
                                                    style={settingInput}
                                                >
                                                    <option value="column">Column (Stacked)</option>
                                                    <option value="column-reverse">Column Reverse</option>
                                                    <option value="row">Row (Wrap)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label style={settingLabel}>Copyright Font Size</label>
                                                <input
                                                    type="text"
                                                    value={settings.mobileFooterCopyrightFontSize || ''}
                                                    onChange={e => update(['settings', 'mobileFooterCopyrightFontSize'], e.target.value)}
                                                    placeholder="0.75rem"
                                                    style={settingInput}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                        <label style={settingLabel}>📱 Mobile-Only Custom CSS (@media ≤ 768px)</label>
                                        <textarea
                                            value={settings.mobileFooterCustomCss || ''}
                                            onChange={e => update(['settings', 'mobileFooterCustomCss'], e.target.value)}
                                            placeholder={`#footer {\n  /* Applied exclusively on mobile screens */\n}\n#footer .footer-column {\n  /* Mobile overrides */\n}`}
                                            rows={5}
                                            style={{
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                padding: '6px 8px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '4px',
                                                fontFamily: 'monospace',
                                                fontSize: '10px',
                                                background: '#0f172a',
                                                color: '#38bdf8',
                                                lineHeight: '1.4',
                                                resize: 'vertical',
                                            }}
                                        />
                                    </div>
                                </>
                            )}

                            {/* TAB 6: CUSTOM CSS */}
                            {activeFooterTab === 'customcss' && (
                                <>
                                    <div>
                                        <label style={settingLabel}>Custom CSS for #footer</label>
                                        <textarea
                                            value={settings.footerCustomCss || ''}
                                            onChange={e => update(['settings', 'footerCustomCss'], e.target.value)}
                                            placeholder={`#footer {\n  /* Custom properties */\n}\n#footer .footer-column {\n  /* Column styles */\n}`}
                                            rows={8}
                                            style={{
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                padding: '6px 8px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '4px',
                                                fontFamily: 'monospace',
                                                fontSize: '10px',
                                                background: '#0f172a',
                                                color: '#38bdf8',
                                                lineHeight: '1.4',
                                                resize: 'vertical',
                                            }}
                                        />
                                        <span style={{ fontSize: '9px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                                            CSS rules entered here are injected directly into the live page.
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </DraggableToolbar>
            )}

            {/* ── Top Footer Search Bar Placement ── */}
            {(settings.showSearchInFooterTop || settings.footerSearchPlacement === 'top') && (
                <div className="container" style={{ marginBottom: '30px', maxWidth: '700px', margin: '0 auto 30px auto', padding: '0 20px', position: 'relative', zIndex: 1 }}>
                    <InlineSearchBar config={config} onSetConfig={onSetConfig} onSaveConfig={onSaveConfig} isEditing={isEditing} compact={false} style={{ width: '100%' }} />
                </div>
            )}

            {/* ── Column Grid ── */}
            {(footer.length > 0 || isEditing) && (
                <div
                    className="container"
                    style={{
                        ...containerStyle,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {footer.map((col, fIndex) => {
                        const isSettingsOpen = openSettings === fIndex;
                        const flexGrow = col.width || 1;
                        const dividerColor = col.dividerColor || 'rgba(255,255,255,0.15)';
                        const dividerWidth = col.dividerWidth || '1px';
                        const colTextColor = col.textColor || settings.footerTextColor || '#ffffff';
                        const colHeadingColor = col.headingColor || settings.footerHeadingColor || colTextColor;
                        const showDivider = !!col.showDivider && fIndex < footer.length - 1 && layout !== 'card-grid' && layout !== 'centered';

                        let colFlexBasis = col.width === 12 ? '1 1 100%' : `${flexGrow} 1 180px`;
                        let customColStyles = {};

                        if (layout === 'centered') {
                            colFlexBasis = '100%';
                            customColStyles = {
                                maxWidth: '800px',
                                margin: '0 auto',
                                textAlign: 'center',
                            };
                        } else if (layout === 'split-brand') {
                            if (fIndex === 0) {
                                colFlexBasis = '2 1 320px';
                                customColStyles = {
                                    background: col.backgroundColor || 'rgba(255,255,255,0.03)',
                                    border: col.border || '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: col.borderRadius || '16px',
                                    padding: col.padding || '24px',
                                    boxShadow: col.boxShadow || '0 8px 32px rgba(0,0,0,0.15)',
                                };
                            } else {
                                colFlexBasis = '1 1 180px';
                            }
                        } else if (layout === 'bar-stacked' || layout === 'rows') {
                            colFlexBasis = '100%';
                            customColStyles = {
                                borderBottom: fIndex < footer.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                paddingBottom: '20px',
                            };
                        } else if (layout === 'rows-grid') {
                            colFlexBasis = '1 1 260px';
                        } else if (layout === '2-rows-split') {
                            if (fIndex === 0) {
                                colFlexBasis = '100%';
                                customColStyles = {
                                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                                    paddingBottom: '24px',
                                    marginBottom: '8px',
                                };
                            } else {
                                colFlexBasis = `${flexGrow} 1 200px`;
                            }
                        } else if (layout === '3-rows-stacked') {
                            if (fIndex === 0) {
                                colFlexBasis = '100%';
                                customColStyles = {
                                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                                    paddingBottom: '20px',
                                };
                            } else if (fIndex === footer.length - 1 && footer.length > 2) {
                                colFlexBasis = '100%';
                                customColStyles = {
                                    borderTop: '1px solid rgba(255,255,255,0.1)',
                                    paddingTop: '20px',
                                };
                            } else {
                                colFlexBasis = `${flexGrow} 1 180px`;
                            }
                        } else if (layout === 'inline-row') {
                            colFlexBasis = '1 1 auto';
                            customColStyles = {
                                padding: '5px 12px',
                                margin: 0,
                            };
                        } else if (layout === 'card-grid') {
                            customColStyles = {
                                background: col.backgroundColor || 'rgba(255,255,255,0.04)',
                                backdropFilter: 'blur(10px)',
                                border: col.border || '1px solid rgba(255,255,255,0.1)',
                                borderRadius: col.borderRadius || '16px',
                                padding: col.padding || '24px',
                                boxShadow: col.boxShadow || '0 10px 30px rgba(0,0,0,0.2)',
                            };
                        } else if (layout === 'compact-bar') {
                            colFlexBasis = '1 1 auto';
                            customColStyles = {
                                padding: '5px 10px',
                                margin: 0,
                            };
                        }

                        // Column card overrides
                        const colCardStyles = {
                            ...(col.backgroundColor ? { background: col.backgroundColor } : {}),
                            ...(col.borderRadius ? { borderRadius: addUnit(col.borderRadius) } : {}),
                            ...(col.border ? { border: col.border } : {}),
                            ...(col.boxShadow ? { boxShadow: col.boxShadow } : {}),
                            ...(col.margin ? { margin: col.margin } : {}),
                            ...(col.textAlign ? { textAlign: col.textAlign } : {}),
                        };

                        const isAccordion = settings.mobileFooterLayout === 'accordion';
                        const isColOpen = !!mobileExpandedCols[fIndex];

                        return (
                            <div
                                key={fIndex}
                                className={`footer-column col-${fIndex}${col.hideOnMobile ? ' footer-col-hide-mobile' : ''}`}
                                style={{
                                    flex: colFlexBasis,
                                    minWidth: 0,
                                    padding: col.padding || (layout === 'card-grid' ? '24px' : '0 24px 24px 0'),
                                    background: col.backgroundColor || 'transparent',
                                    color: colTextColor,
                                    position: 'relative',
                                    boxSizing: 'border-box',
                                    borderRight: showDivider
                                        ? `${dividerWidth} ${col.dividerStyle || 'solid'} ${dividerColor}`
                                        : 'none',
                                    paddingRight: showDivider
                                        ? `calc(${col.padding ? col.padding.split(' ')[1] || '24px' : '24px'} + 16px)`
                                        : undefined,
                                    ...customColStyles,
                                    ...colCardStyles,
                                }}
                            >
                                {/* ── Edit-mode column toolbar ── */}
                                {isEditing && (
                                    <div style={{
                                        display: 'flex',
                                        gap: '4px',
                                        marginBottom: '10px',
                                        background: 'rgba(0,0,0,0.65)',
                                        borderRadius: '6px',
                                        padding: '4px 6px',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        backdropFilter: 'blur(4px)',
                                    }}>
                                        <button
                                            onClick={() => handleMoveCol(fIndex, fIndex - 1)}
                                            disabled={fIndex === 0}
                                            title="Move Left"
                                            style={{ ...ctrlBtn(), opacity: fIndex === 0 ? 0.35 : 1 }}
                                        >←</button>

                                        <button
                                            onClick={() => handleMoveCol(fIndex, fIndex + 1)}
                                            disabled={fIndex === footer.length - 1}
                                            title="Move Right"
                                            style={{ ...ctrlBtn(), opacity: fIndex === footer.length - 1 ? 0.35 : 1 }}
                                        >→</button>

                                        <button
                                            onClick={() => toggleSettings(fIndex)}
                                            title="Column Settings & Styling"
                                            style={{ ...ctrlBtn(isSettingsOpen), display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                        ><LuSettings size={11} /> Style & Options</button>

                                        <button
                                            onClick={() => {
                                                if (window.confirm('Remove this footer column?')) {
                                                    if (openSettings === fIndex) setOpenSettings(null);
                                                    handleRemoveCol(fIndex);
                                                }
                                            }}
                                            title="Delete Column"
                                            style={{ ...ctrlBtn(false, true), display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                        ><LuTrash2 size={11} /></button>

                                        {col.hideOnMobile && (
                                            <span style={{ fontSize: '10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '1px 5px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                📱 Hidden on Mobile
                                            </span>
                                        )}

                                        <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 'auto' }}>
                                            Col {fIndex + 1} ({col.type || 'text'})
                                        </span>
                                    </div>
                                )}

                                {/* ── Column Settings Draggable Toolbar ── */}
                                {isEditing && isSettingsOpen && (
                                    <DraggableToolbar title={`Column ${fIndex + 1} Settings & CSS`} onClose={() => setOpenSettings(null)}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '300px',
                                            maxWidth: '92vw',
                                            maxHeight: '75vh',
                                            background: '#ffffff',
                                            color: '#1e293b',
                                            boxSizing: 'border-box',
                                        }} onClick={e => e.stopPropagation()}>
                                            
                                            {/* Column Setting Tabs */}
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '2px',
                                                background: '#f1f5f9',
                                                padding: '6px 6px 0 6px',
                                                borderBottom: '1px solid #cbd5e1'
                                            }}>
                                                {[
                                                    { id: 'type', label: '📦 Content' },
                                                    { id: 'card', label: '🎨 Card & Box' },
                                                    { id: 'image', label: '🖼️ Logo/Image' },
                                                    { id: 'typography', label: '🔤 Text' },
                                                    { id: 'dividers', label: '📏 Dividers' },
                                                    { id: 'mobile', label: '📱 Mobile' },
                                                ].map(tab => (
                                                    <button
                                                        key={tab.id}
                                                        type="button"
                                                        onClick={() => setColumnActiveTab(tab.id)}
                                                        style={tabBtnStyle(columnActiveTab === tab.id)}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                ))}
                                            </div>

                                            <div style={{
                                                padding: '12px',
                                                overflowY: 'auto',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '10px',
                                                fontSize: '11px'
                                            }}>
                                                {/* COL TAB 1: CONTENT TYPE & SPECIFIC SETTINGS */}
                                                {columnActiveTab === 'type' && (
                                                    <>
                                                        <div>
                                                            <label style={settingLabel}>Column Content Type</label>
                                                            <select
                                                                value={col.type || 'text'}
                                                                onChange={e => update(['footer', fIndex, 'type'], e.target.value)}
                                                                style={settingInput}
                                                            >
                                                                <option value="text">Standard Text / Rich Content</option>
                                                                <option value="links">🔗 Navigation Links / Menu</option>
                                                                <option value="socials">Social Media Icons</option>
                                                                <option value="search">🔍 Search Bar Widget</option>
                                                                <option value="newsletter">✉️ Newsletter Form</option>
                                                                <option value="embed">💻 Embed / Custom Code</option>
                                                            </select>
                                                        </div>

                                                        <div>
                                                            <label style={settingLabel}>Column Width / Span</label>
                                                            <select
                                                                value={col.width || 1}
                                                                onChange={e => update(['footer', fIndex, 'width'], parseFloat(e.target.value))}
                                                                style={settingInput}
                                                            >
                                                                <option value={0.5}>Narrow (½×)</option>
                                                                <option value={1}>Normal (1×)</option>
                                                                <option value={1.5}>Wide (1.5×)</option>
                                                                <option value={2}>Double (2× Span)</option>
                                                                <option value={3}>Triple (3× Span)</option>
                                                                <option value={4}>Quadruple (4× Span)</option>
                                                                <option value={12}>Full Width (100% Span)</option>
                                                            </select>
                                                        </div>

                                                        {/* Links Column Configuration */}
                                                        {col.type === 'links' && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                                                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#2563eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span>🔗 Navigation Links ({col.links?.length || 0})</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const current = col.links || [];
                                                                            update(['footer', fIndex, 'links'], [...current, { label: 'New Link', url: '/', target: '_self' }]);
                                                                        }}
                                                                        style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', padding: '2px 8px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer' }}
                                                                    >
                                                                        + Add Link
                                                                    </button>
                                                                </span>

                                                                {(col.links || []).map((lnk, lIdx) => (
                                                                    <div key={lIdx} style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                            <input
                                                                                type="text"
                                                                                value={lnk.label || ''}
                                                                                onChange={e => {
                                                                                    const updated = [...(col.links || [])];
                                                                                    updated[lIdx] = { ...updated[lIdx], label: e.target.value };
                                                                                    update(['footer', fIndex, 'links'], updated);
                                                                                }}
                                                                                placeholder="Link Label"
                                                                                style={{ ...settingInput, flex: 1, fontWeight: '600' }}
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    if (lIdx > 0) {
                                                                                        const updated = [...(col.links || [])];
                                                                                        const temp = updated[lIdx - 1];
                                                                                        updated[lIdx - 1] = updated[lIdx];
                                                                                        updated[lIdx] = temp;
                                                                                        update(['footer', fIndex, 'links'], updated);
                                                                                    }
                                                                                }}
                                                                                disabled={lIdx === 0}
                                                                                style={{ ...ctrlBtn(), padding: '1px 5px', fontSize: '10px', opacity: lIdx === 0 ? 0.3 : 1 }}
                                                                                title="Move Up"
                                                                            >↑</button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    if (lIdx < (col.links || []).length - 1) {
                                                                                        const updated = [...(col.links || [])];
                                                                                        const temp = updated[lIdx + 1];
                                                                                        updated[lIdx + 1] = updated[lIdx];
                                                                                        updated[lIdx] = temp;
                                                                                        update(['footer', fIndex, 'links'], updated);
                                                                                    }
                                                                                }}
                                                                                disabled={lIdx === (col.links || []).length - 1}
                                                                                style={{ ...ctrlBtn(), padding: '1px 5px', fontSize: '10px', opacity: lIdx === (col.links || []).length - 1 ? 0.3 : 1 }}
                                                                                title="Move Down"
                                                                            >↓</button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    const updated = (col.links || []).filter((_, i) => i !== lIdx);
                                                                                    update(['footer', fIndex, 'links'], updated);
                                                                                }}
                                                                                style={{ ...ctrlBtn(false, true), padding: '1px 5px', fontSize: '10px' }}
                                                                                title="Delete Link"
                                                                            >×</button>
                                                                        </div>
                                                                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                            <input
                                                                                type="text"
                                                                                value={lnk.url || ''}
                                                                                onChange={e => {
                                                                                    const updated = [...(col.links || [])];
                                                                                    updated[lIdx] = { ...updated[lIdx], url: e.target.value };
                                                                                    update(['footer', fIndex, 'links'], updated);
                                                                                }}
                                                                                placeholder="URL (e.g. /shop, /about)"
                                                                                style={{ ...settingInput, flex: 1, fontSize: '10px' }}
                                                                            />
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={lnk.target === '_blank'}
                                                                                    onChange={e => {
                                                                                        const updated = [...(col.links || [])];
                                                                                        updated[lIdx] = { ...updated[lIdx], target: e.target.checked ? '_blank' : '_self' };
                                                                                        update(['footer', fIndex, 'links'], updated);
                                                                                    }}
                                                                                />
                                                                                New Tab
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '6px' }}>
                                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                        <div>
                                                                            <label style={settingLabel}>Bullet Icon</label>
                                                                            <select
                                                                                value={col.linksBulletStyle || 'none'}
                                                                                onChange={e => update(['footer', fIndex, 'linksBulletStyle'], e.target.value)}
                                                                                style={settingInput}
                                                                            >
                                                                                <option value="none">None</option>
                                                                                <option value="chevron">Chevron (›)</option>
                                                                                <option value="bullet">Bullet (•)</option>
                                                                                <option value="dash">Dash (-)</option>
                                                                                <option value="arrow">Arrow (→)</option>
                                                                            </select>
                                                                        </div>
                                                                        <div>
                                                                            <label style={settingLabel}>Orientation</label>
                                                                            <select
                                                                                value={col.linksLayout || 'vertical'}
                                                                                onChange={e => update(['footer', fIndex, 'linksLayout'], e.target.value)}
                                                                                style={settingInput}
                                                                            >
                                                                                <option value="vertical">Vertical List</option>
                                                                                <option value="horizontal">Horizontal Row</option>
                                                                                <option value="grid">2-Col Grid</option>
                                                                            </select>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Link Color</label>
                                                                        <input
                                                                            type="color"
                                                                            value={toValidHex(col.linksColor || col.textColor || settings.footerLinkColor || '#93c5fd')}
                                                                            onChange={e => update(['footer', fIndex, 'linksColor'], e.target.value)}
                                                                            style={{ width: '100%', height: '24px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Hover Color</label>
                                                                        <input
                                                                            type="color"
                                                                            value={toValidHex(col.linksHoverColor || settings.footerLinkHoverColor || '#60a5fa')}
                                                                            onChange={e => update(['footer', fIndex, 'linksHoverColor'], e.target.value)}
                                                                            style={{ width: '100%', height: '24px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Socials Column Configuration */}
                                                        {col.type === 'socials' && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                                                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#2563eb' }}>Social Icons Styling</span>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Alignment</label>
                                                                        <select
                                                                            value={col.socialsAlign || 'left'}
                                                                            onChange={e => update(['footer', fIndex, 'socialsAlign'], e.target.value)}
                                                                            style={settingInput}
                                                                        >
                                                                            <option value="left">Left</option>
                                                                            <option value="center">Center</option>
                                                                            <option value="right">Right</option>
                                                                            <option value="space-between">Space Between</option>
                                                                        </select>
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Icon Style</label>
                                                                        <select
                                                                            value={col.socialsStyle || 'circles'}
                                                                            onChange={e => update(['footer', fIndex, 'socialsStyle'], e.target.value)}
                                                                            style={settingInput}
                                                                        >
                                                                            <option value="circles">Circles</option>
                                                                            <option value="rounded">Rounded Squares</option>
                                                                            <option value="minimal">Minimal (No Bg)</option>
                                                                            <option value="cards">Pills / Cards</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Icon Size</label>
                                                                        <select
                                                                            value={col.socialsSize || '40px'}
                                                                            onChange={e => update(['footer', fIndex, 'socialsSize'], e.target.value)}
                                                                            style={settingInput}
                                                                        >
                                                                            <option value="32px">Small (32px)</option>
                                                                            <option value="40px">Medium (40px)</option>
                                                                            <option value="48px">Large (48px)</option>
                                                                        </select>
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Icon Gap</label>
                                                                        <input
                                                                            type="text"
                                                                            value={col.socialsGap || '12px'}
                                                                            onChange={e => update(['footer', fIndex, 'socialsGap'], e.target.value)}
                                                                            style={settingInput}
                                                                            placeholder="12px"
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Icon Color</label>
                                                                        <input
                                                                            type="color"
                                                                            value={toValidHex(col.socialsColor || '#ffffff')}
                                                                            onChange={e => update(['footer', fIndex, 'socialsColor'], e.target.value)}
                                                                            style={{ width: '100%', height: '24px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Icon Background</label>
                                                                        <input
                                                                            type="color"
                                                                            value={toValidHex(col.socialsBg || 'rgba(255,255,255,0.1)')}
                                                                            onChange={e => update(['footer', fIndex, 'socialsBg'], e.target.value)}
                                                                            style={{ width: '100%', height: '24px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Embed Code Configuration */}
                                                        {col.type === 'embed' && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                                                <label style={settingLabel}>Custom HTML / Embed Code</label>
                                                                <textarea
                                                                    value={col.embedCode || ''}
                                                                    onChange={e => update(['footer', fIndex, 'embedCode'], e.target.value)}
                                                                    placeholder="<div>Custom HTML or widget iframe</div>"
                                                                    rows={4}
                                                                    style={{ ...settingInput, height: 'auto', fontFamily: 'monospace', fontSize: '10px' }}
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* COL TAB 2: CARD BOX & LAYOUT STYLING */}
                                                {columnActiveTab === 'card' && (
                                                    <>
                                                        <div>
                                                            <label style={settingLabel}>Column Background Color</label>
                                                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                <input
                                                                    type="color"
                                                                    value={toValidHex(col.backgroundColor || '#1a1a1a')}
                                                                    onChange={e => update(['footer', fIndex, 'backgroundColor'], e.target.value)}
                                                                    style={{ width: '32px', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={col.backgroundColor || ''}
                                                                    onChange={e => update(['footer', fIndex, 'backgroundColor'], e.target.value)}
                                                                    placeholder="transparent or rgba(...)"
                                                                    style={{ ...settingInput, flex: 1 }}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                            <div>
                                                                <label style={settingLabel}>Border (CSS)</label>
                                                                <input
                                                                    type="text"
                                                                    value={col.border || ''}
                                                                    onChange={e => update(['footer', fIndex, 'border'], e.target.value)}
                                                                    placeholder="1px solid rgba(255,255,255,0.1)"
                                                                    style={settingInput}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={settingLabel}>Border Radius</label>
                                                                <input
                                                                    type="text"
                                                                    value={col.borderRadius || ''}
                                                                    onChange={e => update(['footer', fIndex, 'borderRadius'], e.target.value)}
                                                                    placeholder="8px or 16px"
                                                                    style={settingInput}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label style={settingLabel}>Box Shadow</label>
                                                            <input
                                                                type="text"
                                                                value={col.boxShadow || ''}
                                                                onChange={e => update(['footer', fIndex, 'boxShadow'], e.target.value)}
                                                                placeholder="0 8px 24px rgba(0,0,0,0.2)"
                                                                style={settingInput}
                                                            />
                                                        </div>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                            <div>
                                                                <label style={settingLabel}>Padding</label>
                                                                <input
                                                                    type="text"
                                                                    value={col.padding || ''}
                                                                    onChange={e => update(['footer', fIndex, 'padding'], e.target.value)}
                                                                    placeholder="16px (or 0 24px 24px 0)"
                                                                    style={settingInput}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={settingLabel}>Margin</label>
                                                                <input
                                                                    type="text"
                                                                    value={col.margin || ''}
                                                                    onChange={e => update(['footer', fIndex, 'margin'], e.target.value)}
                                                                    placeholder="0px"
                                                                    style={settingInput}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label style={settingLabel}>Text Align</label>
                                                            <select
                                                                value={col.textAlign || 'left'}
                                                                onChange={e => update(['footer', fIndex, 'textAlign'], e.target.value)}
                                                                style={settingInput}
                                                            >
                                                                <option value="left">Left</option>
                                                                <option value="center">Center</option>
                                                                <option value="right">Right</option>
                                                            </select>
                                                        </div>
                                                    </>
                                                )}

                                                {/* COL TAB 3: IMAGE / LOGO */}
                                                {columnActiveTab === 'image' && (
                                                    <>
                                                        <div>
                                                            <label style={settingLabel}>Column Image / Logo</label>
                                                            <MediaInput
                                                                size="small"
                                                                value={col.image || ''}
                                                                onChange={e => update(['footer', fIndex, 'image'], e?.target ? e.target.value : e)}
                                                                placeholder="Upload or paste image URL"
                                                            />
                                                        </div>

                                                        {col.image && (
                                                            <>
                                                                <div>
                                                                    <label style={settingLabel}>Image Alt Text</label>
                                                                    <input
                                                                        type="text"
                                                                        value={col.imageAlt || ''}
                                                                        onChange={e => update(['footer', fIndex, 'imageAlt'], e.target.value)}
                                                                        placeholder="Descriptive alt text"
                                                                        style={settingInput}
                                                                    />
                                                                </div>

                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Image Width</label>
                                                                        <input
                                                                            type="text"
                                                                            value={col.imageWidth || '100%'}
                                                                            onChange={e => update(['footer', fIndex, 'imageWidth'], e.target.value)}
                                                                            style={settingInput}
                                                                            placeholder="100% or 160px"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Image Alignment</label>
                                                                        <select
                                                                            value={col.imageAlign || 'left'}
                                                                            onChange={e => update(['footer', fIndex, 'imageAlign'], e.target.value)}
                                                                            style={settingInput}
                                                                        >
                                                                            <option value="left">Left</option>
                                                                            <option value="center">Center</option>
                                                                            <option value="right">Right</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Border Radius</label>
                                                                        <input
                                                                            type="text"
                                                                            value={col.imageRadius || ''}
                                                                            onChange={e => update(['footer', fIndex, 'imageRadius'], e.target.value)}
                                                                            placeholder="0px (e.g. 8px)"
                                                                            style={settingInput}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Clickable Link URL</label>
                                                                        <input
                                                                            type="text"
                                                                            value={col.imageLink || ''}
                                                                            onChange={e => update(['footer', fIndex, 'imageLink'], e.target.value)}
                                                                            placeholder="/"
                                                                            style={settingInput}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </>
                                                )}

                                                {/* COL TAB 4: TYPOGRAPHY OVERRIDES */}
                                                {columnActiveTab === 'typography' && (
                                                    <ColumnTypographyControls
                                                        values={col}
                                                        onChange={(field, val) => update(['footer', fIndex, field], val)}
                                                        customFonts={config?.customFonts || settings?.customFonts}
                                                    />
                                                )}

                                                {/* COL TAB 5: DIVIDERS */}
                                                {columnActiveTab === 'dividers' && (
                                                    <>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            {/* Right Vertical Divider */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`footer-divider-${fIndex}`}
                                                                    checked={!!col.showDivider}
                                                                    onChange={e => update(['footer', fIndex, 'showDivider'], e.target.checked)}
                                                                    style={{ accentColor: '#3b82f6', cursor: 'pointer' }}
                                                                />
                                                                <label htmlFor={`footer-divider-${fIndex}`} style={{ ...settingLabel, margin: 0, cursor: 'pointer' }}>
                                                                    Right vertical dividing line
                                                                </label>
                                                            </div>
                                                            {col.showDivider && (
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Divider Width</label>
                                                                        <input
                                                                            type="text"
                                                                            value={col.dividerWidth || '1px'}
                                                                            onChange={e => update(['footer', fIndex, 'dividerWidth'], e.target.value)}
                                                                            style={settingInput}
                                                                            placeholder="1px"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Divider Color</label>
                                                                        <input
                                                                            type="color"
                                                                            value={toValidHex(col.dividerColor || 'rgba(255,255,255,0.15)')}
                                                                            onChange={e => update(['footer', fIndex, 'dividerColor'], e.target.value)}
                                                                            style={{ width: '100%', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Bottom Horizontal Divider */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`footer-h-divider-${fIndex}`}
                                                                    checked={!!col.showHorizontalDivider}
                                                                    onChange={e => update(['footer', fIndex, 'showHorizontalDivider'], e.target.checked)}
                                                                    style={{ accentColor: '#3b82f6', cursor: 'pointer' }}
                                                                />
                                                                <label htmlFor={`footer-h-divider-${fIndex}`} style={{ ...settingLabel, margin: 0, cursor: 'pointer' }}>
                                                                    Bottom horizontal dividing line
                                                                </label>
                                                            </div>
                                                            {col.showHorizontalDivider && (
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                    <div>
                                                                        <label style={settingLabel}>Line Width</label>
                                                                        <input
                                                                            type="text"
                                                                            value={col.horizontalDividerWidth || '1px'}
                                                                            onChange={e => update(['footer', fIndex, 'horizontalDividerWidth'], e.target.value)}
                                                                            style={settingInput}
                                                                            placeholder="1px"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label style={settingLabel}>Line Color</label>
                                                                        <input
                                                                            type="color"
                                                                            value={toValidHex(col.horizontalDividerColor || col.dividerColor || 'rgba(255,255,255,0.15)')}
                                                                            onChange={e => update(['footer', fIndex, 'horizontalDividerColor'], e.target.value)}
                                                                            style={{ width: '100%', height: '26px', padding: 0, border: '1px solid #475569', borderRadius: '4px', cursor: 'pointer' }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}

                                                {/* COL TAB 6: MOBILE COLUMN CONFIGURATION */}
                                                {columnActiveTab === 'mobile' && (
                                                    <>
                                                        <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                                            <label style={{ ...settingLabel, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0, fontWeight: 'bold' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!col.hideOnMobile}
                                                                    onChange={e => update(['footer', fIndex, 'hideOnMobile'], e.target.checked)}
                                                                    style={{ width: '16px', height: '16px', accentColor: '#ef4444' }}
                                                                />
                                                                <span>📱 Hide Column on Mobile (≤ 768px)</span>
                                                            </label>
                                                            <p style={{ margin: '4px 0 0 24px', fontSize: '10px', color: '#64748b' }}>
                                                                Completely removes this column on mobile devices and small screens.
                                                            </p>
                                                        </div>

                                                        <div>
                                                            <label style={settingLabel}>Mobile Width Override</label>
                                                            <select
                                                                value={col.mobileWidth || ''}
                                                                onChange={e => update(['footer', fIndex, 'mobileWidth'], e.target.value)}
                                                                style={settingInput}
                                                            >
                                                                <option value="">Default (Auto / Layout Preset)</option>
                                                                <option value="100%">100% (Full Width Block)</option>
                                                                <option value="50%">50% (Two Columns Side-by-Side)</option>
                                                                <option value="33.333%">33% (Three Columns)</option>
                                                                <option value="auto">Auto (Fit Content)</option>
                                                            </select>
                                                        </div>

                                                        <div>
                                                            <label style={settingLabel}>Mobile Text Alignment</label>
                                                            <select
                                                                value={col.mobileTextAlign || 'inherit'}
                                                                onChange={e => update(['footer', fIndex, 'mobileTextAlign'], e.target.value)}
                                                                style={settingInput}
                                                            >
                                                                <option value="inherit">Inherit Global Mobile Footer Alignment</option>
                                                                <option value="left">Left</option>
                                                                <option value="center">Center</option>
                                                                <option value="right">Right</option>
                                                            </select>
                                                        </div>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                            <div>
                                                                <label style={settingLabel}>Mobile Order</label>
                                                                <input
                                                                    type="number"
                                                                    value={col.mobileOrder !== undefined ? col.mobileOrder : ''}
                                                                    onChange={e => update(['footer', fIndex, 'mobileOrder'], e.target.value)}
                                                                    style={settingInput}
                                                                    placeholder="e.g. 1, 2, -1"
                                                                />
                                                                <span style={{ fontSize: '9px', color: '#94a3b8' }}>Reorder on mobile (flex order)</span>
                                                            </div>
                                                            <div>
                                                                <label style={settingLabel}>Mobile Margin Bottom</label>
                                                                <input
                                                                    type="text"
                                                                    value={col.mobileMarginBottom || ''}
                                                                    onChange={e => update(['footer', fIndex, 'mobileMarginBottom'], e.target.value)}
                                                                    style={settingInput}
                                                                    placeholder="e.g. 16px"
                                                                />
                                                                <span style={{ fontSize: '9px', color: '#94a3b8' }}>Space below on mobile</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </DraggableToolbar>
                                )}

                                {/* Column Image / Logo */}
                                {col.image && (
                                    <div style={{ textAlign: col.imageAlign || 'left', marginBottom: '14px' }}>
                                        {col.imageLink ? (
                                            <a href={col.imageLink} target={col.imageLink.startsWith('http') ? '_blank' : undefined} rel="noreferrer" onClick={isEditing ? (e) => e.preventDefault() : undefined}>
                                                <img
                                                    src={resolveMediaUrl(col.image)}
                                                    alt={col.imageAlt || ''}
                                                    style={{
                                                        width: col.imageWidth || '100%',
                                                        maxWidth: '100%',
                                                        borderRadius: col.imageRadius ? addUnit(col.imageRadius) : '0px',
                                                        display: 'inline-block',
                                                        height: 'auto',
                                                    }}
                                                    onError={e => { e.target.onerror = null; }}
                                                />
                                            </a>
                                        ) : (
                                            <img
                                                src={resolveMediaUrl(col.image)}
                                                alt={col.imageAlt || ''}
                                                style={{
                                                    width: col.imageWidth || '100%',
                                                    maxWidth: '100%',
                                                    borderRadius: col.imageRadius ? addUnit(col.imageRadius) : '0px',
                                                    display: 'inline-block',
                                                    height: 'auto',
                                                }}
                                                onError={e => { e.target.onerror = null; }}
                                            />
                                        )}
                                    </div>
                                )}

                                {/* ── Content Display: Newsletter Column Widget ── */}
                                {col.type === 'newsletter' ? (
                                    <div>
                                        {(col.heading || isEditing || isAccordion) && (
                                            <div
                                                className={isAccordion ? 'footer-accordion-header' : undefined}
                                                onClick={isAccordion && !isEditing ? () => toggleMobileCol(fIndex) : undefined}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                                            >
                                                <h4 style={{
                                                    fontSize: col.headingSize ? addUnit(col.headingSize) : '1.1rem',
                                                    margin: isAccordion ? 0 : '0 0 12px 0',
                                                    fontFamily: col.headingFontFamily || settings.footerHeadingFontFamily || 'inherit',
                                                    color: col.headingColor || colHeadingColor,
                                                    flex: 1
                                                }}>
                                                    {col.heading || 'Newsletter'}
                                                </h4>
                                                {isAccordion && (
                                                    <span
                                                        className="footer-accordion-indicator"
                                                        onClick={(e) => { e.stopPropagation(); toggleMobileCol(fIndex); }}
                                                    >
                                                        {isColOpen ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className={`footer-col-body ${isAccordion ? (isColOpen ? 'footer-accordion-body-open' : 'footer-accordion-body-closed') : ''}`}>
                                            <NewsletterWidget
                                                config={config}
                                                onUpdateConfig={(newCfg) => {
                                                    update(['modules', 'mailService'], newCfg?.modules?.mailService);
                                                }}
                                                variant="compact"
                                                title=""
                                                subtitle={col.text}
                                                isEditing={isEditing}
                                            />
                                        </div>
                                    </div>
                                ) : col.type === 'search' ? (
                                    <div>
                                        {(col.heading || isEditing || isAccordion) && (
                                            <div
                                                className={isAccordion ? 'footer-accordion-header' : undefined}
                                                onClick={isAccordion && !isEditing ? () => toggleMobileCol(fIndex) : undefined}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                                            >
                                                <h4 style={{
                                                    fontSize: col.headingSize ? addUnit(col.headingSize) : '1.1rem',
                                                    margin: isAccordion ? 0 : '0 0 12px 0',
                                                    fontFamily: col.headingFontFamily || settings.footerHeadingFontFamily || 'inherit',
                                                    color: col.headingColor || colHeadingColor,
                                                    flex: 1
                                                }}>
                                                    {col.heading || '🔍 Search Site'}
                                                </h4>
                                                {isAccordion && (
                                                    <span
                                                        className="footer-accordion-indicator"
                                                        onClick={(e) => { e.stopPropagation(); toggleMobileCol(fIndex); }}
                                                    >
                                                        {isColOpen ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className={`footer-col-body ${isAccordion ? (isColOpen ? 'footer-accordion-body-open' : 'footer-accordion-body-closed') : ''}`}>
                                            <InlineSearchBar config={config} onSetConfig={onSetConfig} onSaveConfig={onSaveConfig} isEditing={isEditing} compact={true} style={{ width: '100%' }} />
                                        </div>
                                    </div>
                                ) : col.type === 'embed' ? (
                                    <div>
                                        {(col.heading || isEditing || isAccordion) && (
                                            <div
                                                className={isAccordion ? 'footer-accordion-header' : undefined}
                                                onClick={isAccordion && !isEditing ? () => toggleMobileCol(fIndex) : undefined}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <Editable
                                                        as="h4"
                                                        isEditing={isEditing}
                                                        value={col.heading || ''}
                                                        onChange={e => update(['footer', fIndex, 'heading'], e.target.value)}
                                                        onStyleChange={(p, v) => handleHeadingStyleChange(fIndex, p, v)}
                                                        currentStyles={{
                                                            align: col.headingAlign,
                                                            size: col.headingSize || col.headingFontSize,
                                                            color: col.headingColor || colHeadingColor,
                                                            fontFamily: col.headingFontFamily,
                                                            preset: col.headingPreset,
                                                            weight: col.headingWeight || col.headingFontWeight,
                                                            fontWeight: col.headingFontWeight || col.headingWeight,
                                                            letterSpacing: col.headingLetterSpacing,
                                                            lineHeight: col.headingLineHeight,
                                                            fontStyle: col.headingFontStyle,
                                                            textDecoration: col.headingTextDecoration,
                                                            transform: col.headingTransform || col.headingTextTransform,
                                                            textTransform: col.headingTextTransform || col.headingTransform,
                                                        }}
                                                        style={{
                                                            ...((_getFontStyles || getFontStyles) ? (_getFontStyles || getFontStyles)('h2', {
                                                                align: col.headingAlign,
                                                                size: col.headingSize || col.headingFontSize,
                                                                color: col.headingColor || colHeadingColor,
                                                                fontFamily: col.headingFontFamily,
                                                                preset: col.headingPreset,
                                                                weight: col.headingWeight || col.headingFontWeight,
                                                                fontWeight: col.headingFontWeight || col.headingWeight,
                                                                letterSpacing: col.headingLetterSpacing,
                                                                lineHeight: col.headingLineHeight,
                                                                fontStyle: col.headingFontStyle,
                                                                textDecoration: col.headingTextDecoration,
                                                                textTransform: col.headingTextTransform || col.headingTransform,
                                                            }) : {}),
                                                            fontSize: (col.headingSize || col.headingFontSize) ? addUnit(col.headingSize || col.headingFontSize) : '1.2rem',
                                                            fontWeight: col.headingWeight || col.headingFontWeight || undefined,
                                                            letterSpacing: col.headingLetterSpacing ? addUnit(col.headingLetterSpacing) : undefined,
                                                            lineHeight: col.headingLineHeight || undefined,
                                                            fontFamily: col.headingFontFamily || settings.footerHeadingFontFamily || 'inherit',
                                                            margin: isAccordion ? 0 : '0 0 14px 0',
                                                        }}
                                                    />
                                                </div>
                                                {isAccordion && (
                                                    <span
                                                        className="footer-accordion-indicator"
                                                        onClick={(e) => { e.stopPropagation(); toggleMobileCol(fIndex); }}
                                                    >
                                                        {isColOpen ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className={`footer-col-body ${isAccordion ? (isColOpen ? 'footer-accordion-body-open' : 'footer-accordion-body-closed') : ''}`}>
                                            {col.embedCode ? (
                                                <div dangerouslySetInnerHTML={{ __html: col.embedCode }} />
                                            ) : (
                                                isEditing && (
                                                    <div style={{ padding: '12px', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '6px', textAlign: 'center', fontSize: '11px', color: '#94a3b8' }}>
                                                        Click ⚙ to enter Custom HTML or Embed code.
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                ) : col.type === 'links' ? (
                                    <div>
                                        {(col.heading || isEditing || isAccordion) && (
                                            <div
                                                className={isAccordion ? 'footer-accordion-header' : undefined}
                                                onClick={isAccordion && !isEditing ? () => toggleMobileCol(fIndex) : undefined}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <Editable
                                                        as="h4"
                                                        isEditing={isEditing}
                                                        value={col.heading || ''}
                                                        onChange={e => update(['footer', fIndex, 'heading'], e.target.value)}
                                                        onStyleChange={(p, v) => handleHeadingStyleChange(fIndex, p, v)}
                                                        currentStyles={{
                                                            align: col.headingAlign,
                                                            size: col.headingSize || col.headingFontSize,
                                                            color: col.headingColor || colHeadingColor,
                                                            fontFamily: col.headingFontFamily,
                                                            preset: col.headingPreset,
                                                            weight: col.headingWeight || col.headingFontWeight,
                                                            fontWeight: col.headingFontWeight || col.headingWeight,
                                                            letterSpacing: col.headingLetterSpacing,
                                                            lineHeight: col.headingLineHeight,
                                                            fontStyle: col.headingFontStyle,
                                                            textDecoration: col.headingTextDecoration,
                                                            transform: col.headingTransform || col.headingTextTransform,
                                                            textTransform: col.headingTextTransform || col.headingTransform,
                                                        }}
                                                        style={{
                                                            ...((_getFontStyles || getFontStyles) ? (_getFontStyles || getFontStyles)('h2', {
                                                                align: col.headingAlign,
                                                                size: col.headingSize || col.headingFontSize,
                                                                color: col.headingColor || colHeadingColor,
                                                                fontFamily: col.headingFontFamily,
                                                                preset: col.headingPreset,
                                                                weight: col.headingWeight || col.headingFontWeight,
                                                                fontWeight: col.headingFontWeight || col.headingWeight,
                                                                letterSpacing: col.headingLetterSpacing,
                                                                lineHeight: col.headingLineHeight,
                                                                fontStyle: col.headingFontStyle,
                                                                textDecoration: col.headingTextDecoration,
                                                                textTransform: col.headingTextTransform || col.headingTransform,
                                                            }) : {}),
                                                            fontSize: (col.headingSize || col.headingFontSize) ? addUnit(col.headingSize || col.headingFontSize) : '1.2rem',
                                                            fontWeight: col.headingWeight || col.headingFontWeight || undefined,
                                                            letterSpacing: col.headingLetterSpacing ? addUnit(col.headingLetterSpacing) : undefined,
                                                            lineHeight: col.headingLineHeight || undefined,
                                                            fontFamily: col.headingFontFamily || settings.footerHeadingFontFamily || 'inherit',
                                                            margin: isAccordion ? 0 : '0 0 14px 0',
                                                        }}
                                                    />
                                                </div>
                                                {isAccordion && (
                                                    <span
                                                        className="footer-accordion-indicator"
                                                        onClick={(e) => { e.stopPropagation(); toggleMobileCol(fIndex); }}
                                                    >
                                                        {isColOpen ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className={`footer-col-body ${isAccordion ? (isColOpen ? 'footer-accordion-body-open' : 'footer-accordion-body-closed') : ''}`}>
                                        {(!col.links || col.links.length === 0) ? (
                                            isEditing ? (
                                                <div style={{ padding: '12px', border: '1px dashed rgba(255,255,255,0.25)', borderRadius: '6px', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#cbd5e1', display: 'block', marginBottom: '8px' }}>
                                                        No links in this column yet.
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const defaultLinks = [
                                                                { label: 'Home', url: '/' },
                                                                { label: 'Shop All', url: '/shop' },
                                                                { label: 'About Us', url: '/about' },
                                                                { label: 'Contact', url: '/contact' }
                                                            ];
                                                            update(['footer', fIndex, 'links'], defaultLinks);
                                                        }}
                                                        style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                                                    >
                                                        + Add Quick Links Presets
                                                    </button>
                                                </div>
                                            ) : null
                                        ) : (
                                            <ul style={{
                                                listStyle: 'none',
                                                padding: 0,
                                                margin: 0,
                                                display: col.linksLayout === 'horizontal' ? 'flex' : col.linksLayout === 'grid' ? 'grid' : 'flex',
                                                gridTemplateColumns: col.linksLayout === 'grid' ? 'repeat(auto-fit, minmax(120px, 1fr))' : undefined,
                                                flexDirection: col.linksLayout === 'horizontal' ? 'row' : 'column',
                                                flexWrap: col.linksLayout === 'horizontal' ? 'wrap' : undefined,
                                                gap: col.linksGap || '10px',
                                                alignItems: col.linksLayout === 'horizontal' ? 'center' : 'flex-start',
                                            }}>
                                                {(col.links || []).map((linkItem, lIdx) => {
                                                    const rawLabel = linkItem.label || linkItem.title || 'Link';
                                                    const label = typeof rawLabel === 'string' ? stripHtml(rawLabel) : rawLabel;
                                                    const rawUrl = linkItem.url || '#';
                                                    const isFile = isFileUrl(rawUrl);
                                                    const normalizedUrl = isFile ? resolveMediaUrl(rawUrl) : normalizeAppUrl(rawUrl);
                                                    const hasHash = !isFile && (rawUrl.includes('#') || (typeof normalizedUrl === 'string' && normalizedUrl.startsWith('#')));
                                                    const isExternal = isFile || (!hasHash && (
                                                        isExternalOrFileUrl(rawUrl) ||
                                                        isExternalOrFileUrl(normalizedUrl)
                                                    ));
                                                    const target = linkItem.target || (isExternal || isFile ? '_blank' : '_self');
                                                    const bulletStyle = col.linksBulletStyle || 'none';
                                                    let bulletChar = null;
                                                    if (bulletStyle === 'chevron') bulletChar = '› ';
                                                    else if (bulletStyle === 'bullet') bulletChar = '• ';
                                                    else if (bulletStyle === 'dash') bulletChar = '- ';
                                                    else if (bulletStyle === 'arrow') bulletChar = '→ ';

                                                    const linkColor = col.linksColor || colTextColor || settings.footerLinkColor || 'inherit';
                                                    const hoverColor = col.linksHoverColor || settings.footerLinkHoverColor || settings.primaryColor || '#60a5fa';

                                                    const linkContent = (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                            {bulletChar && <span style={{ opacity: 0.7, fontSize: '0.9em' }}>{bulletChar}</span>}
                                                            <span>{label}</span>
                                                        </span>
                                                    );

                                                    const linkStyle = {
                                                        color: linkColor,
                                                        textDecoration: 'none',
                                                        fontSize: col.textSize ? addUnit(col.textSize) : '0.9rem',
                                                        fontFamily: col.textFontFamily || settings.footerBodyFontFamily || 'inherit',
                                                        transition: 'all 0.2s ease',
                                                        display: 'inline-block',
                                                        opacity: 0.9,
                                                    };

                                                    const handleHoverIn = (e) => {
                                                        e.currentTarget.style.color = hoverColor;
                                                        e.currentTarget.style.opacity = '1';
                                                        e.currentTarget.style.transform = 'translateX(3px)';
                                                    };

                                                    const handleHoverOut = (e) => {
                                                        e.currentTarget.style.color = linkColor;
                                                        e.currentTarget.style.opacity = '0.9';
                                                        e.currentTarget.style.transform = 'none';
                                                    };

                                                    if (hasHash) {
                                                        return (
                                                            <li key={lIdx} style={{ margin: 0 }}>
                                                                <a
                                                                    href={getTenantFullUrl(rawUrl)}
                                                                    style={linkStyle}
                                                                    onMouseOver={handleHoverIn}
                                                                    onMouseOut={handleHoverOut}
                                                                    onClick={(e) => {
                                                                        const handled = handleAppUrlClick(e, rawUrl, isEditing);
                                                                        if (!handled && !isEditing) {
                                                                            const hashIndex = rawUrl.indexOf('#');
                                                                            const hashPart = hashIndex !== -1 ? rawUrl.substring(hashIndex + 1).replace(/^#/, '') : '';
                                                                            if (hashPart) {
                                                                                navigate('/', { state: { targetId: hashPart } });
                                                                            } else {
                                                                                navigate('/');
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {linkContent}
                                                                </a>
                                                            </li>
                                                        );
                                                    }

                                                    if (isExternal) {
                                                        return (
                                                            <li key={lIdx} style={{ margin: 0 }}>
                                                                <a
                                                                    href={normalizedUrl}
                                                                    target={target}
                                                                    rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                                                                    style={linkStyle}
                                                                    onMouseOver={handleHoverIn}
                                                                    onMouseOut={handleHoverOut}
                                                                    onClick={(e) => {
                                                                        if (isEditing) e.preventDefault();
                                                                    }}
                                                                >
                                                                    {linkContent}
                                                                </a>
                                                            </li>
                                                        );
                                                    }

                                                    if (target === '_blank') {
                                                        return (
                                                            <li key={lIdx} style={{ margin: 0 }}>
                                                                <a
                                                                    href={getTenantFullUrl(rawUrl)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={linkStyle}
                                                                    onMouseOver={handleHoverIn}
                                                                    onMouseOut={handleHoverOut}
                                                                    onClick={(e) => {
                                                                        if (isEditing) e.preventDefault();
                                                                    }}
                                                                >
                                                                    {linkContent}
                                                                </a>
                                                            </li>
                                                        );
                                                    }

                                                    return (
                                                        <li key={lIdx} style={{ margin: 0 }}>
                                                            <Link
                                                                to={normalizedUrl}
                                                                style={linkStyle}
                                                                onMouseOver={handleHoverIn}
                                                                onMouseOut={handleHoverOut}
                                                                onClick={(e) => {
                                                                    if (isEditing) e.preventDefault();
                                                                }}
                                                            >
                                                                {linkContent}
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                        </div>
                                    </div>
                                ) : col.type === 'socials' ? (
                                    <div>
                                        {(col.heading || isEditing || isAccordion) && (
                                            <div
                                                className={isAccordion ? 'footer-accordion-header' : undefined}
                                                onClick={isAccordion && !isEditing ? () => toggleMobileCol(fIndex) : undefined}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <Editable
                                                        as="h4"
                                                        isEditing={isEditing}
                                                        value={col.heading || ''}
                                                        onChange={e => update(['footer', fIndex, 'heading'], e.target.value)}
                                                        onStyleChange={(p, v) => handleHeadingStyleChange(fIndex, p, v)}
                                                        currentStyles={{
                                                            align: col.headingAlign || col.socialsAlign || 'left',
                                                            size: col.headingSize || col.headingFontSize,
                                                            color: col.headingColor || colHeadingColor,
                                                            fontFamily: col.headingFontFamily,
                                                            preset: col.headingPreset,
                                                            weight: col.headingWeight || col.headingFontWeight,
                                                            fontWeight: col.headingFontWeight || col.headingWeight,
                                                            letterSpacing: col.headingLetterSpacing,
                                                            lineHeight: col.headingLineHeight,
                                                            fontStyle: col.headingFontStyle,
                                                            textDecoration: col.headingTextDecoration,
                                                            transform: col.headingTransform || col.headingTextTransform,
                                                            textTransform: col.headingTextTransform || col.headingTransform,
                                                        }}
                                                        style={{
                                                            ...((_getFontStyles || getFontStyles) ? (_getFontStyles || getFontStyles)('h2', {
                                                                align: col.headingAlign || col.socialsAlign || 'left',
                                                                size: col.headingSize || col.headingFontSize,
                                                                color: col.headingColor || colHeadingColor,
                                                                fontFamily: col.headingFontFamily,
                                                                preset: col.headingPreset,
                                                                weight: col.headingWeight || col.headingFontWeight,
                                                                fontWeight: col.headingFontWeight || col.headingWeight,
                                                                letterSpacing: col.headingLetterSpacing,
                                                                lineHeight: col.headingLineHeight,
                                                                fontStyle: col.headingFontStyle,
                                                                textDecoration: col.headingTextDecoration,
                                                                textTransform: col.headingTextTransform || col.headingTransform,
                                                            }) : {}),
                                                            fontSize: (col.headingSize || col.headingFontSize) ? addUnit(col.headingSize || col.headingFontSize) : '1.2rem',
                                                            fontWeight: col.headingWeight || col.headingFontWeight || undefined,
                                                            letterSpacing: col.headingLetterSpacing ? addUnit(col.headingLetterSpacing) : undefined,
                                                            lineHeight: col.headingLineHeight || undefined,
                                                            fontFamily: col.headingFontFamily || settings.footerHeadingFontFamily || 'inherit',
                                                            margin: isAccordion ? 0 : '0 0 14px 0',
                                                        }}
                                                    />
                                                </div>
                                                {isAccordion && (
                                                    <span
                                                        className="footer-accordion-indicator"
                                                        onClick={(e) => { e.stopPropagation(); toggleMobileCol(fIndex); }}
                                                    >
                                                        {isColOpen ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className={`footer-col-body ${isAccordion ? (isColOpen ? 'footer-accordion-body-open' : 'footer-accordion-body-closed') : ''}`}>
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: col.socialsGap || '12px',
                                                justifyContent: col.socialsAlign === 'center' ? 'center' : col.socialsAlign === 'right' ? 'flex-end' : col.socialsAlign === 'space-between' ? 'space-between' : 'flex-start',
                                                alignItems: 'center',
                                            }}>
                                                {(socials || []).map((s, i) => {
                                                    const iconSize = col.socialsSize || '40px';
                                                    const styleMode = col.socialsStyle || 'circles';
                                                    const isMinimal = styleMode === 'minimal';
                                                    const isRounded = styleMode === 'rounded';
                                                    const isCard = styleMode === 'cards';
                                                    const numSize = parseInt(iconSize, 10) || 40;

                                                    return (
                                                        <a
                                                            key={i}
                                                            href={s.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            onClick={isEditing ? (e) => e.preventDefault() : undefined}
                                                            style={{
                                                                color: col.socialsColor || colTextColor || 'white',
                                                                textDecoration: 'none',
                                                                background: isMinimal ? 'transparent' : (col.socialsBg || 'rgba(255,255,255,0.1)'),
                                                                width: isCard ? 'auto' : iconSize,
                                                                height: iconSize,
                                                                padding: isCard ? '0 14px' : 0,
                                                                borderRadius: isMinimal ? '0px' : isRounded ? '8px' : isCard ? '20px' : '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: isCard ? '8px' : 0,
                                                                border: isMinimal ? 'none' : '1px solid rgba(255,255,255,0.2)',
                                                                transition: 'all 0.2s ease',
                                                                fontSize: '1rem',
                                                            }}
                                                            onMouseOver={e => {
                                                                if (settings.primaryColor && !isMinimal) e.currentTarget.style.background = settings.primaryColor;
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }}
                                                            onMouseOut={e => {
                                                                if (!isMinimal) e.currentTarget.style.background = col.socialsBg || 'rgba(255,255,255,0.1)';
                                                                e.currentTarget.style.transform = 'none';
                                                            }}
                                                        >
                                                            {renderIcon(s.platform, Math.round(numSize * 0.5))}
                                                            {isCard && <span style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>{s.platform}</span>}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Standard Text & Rich Content Column */
                                    <div>
                                        {(col.heading || isEditing || isAccordion) && (
                                            <div
                                                className={isAccordion ? 'footer-accordion-header' : undefined}
                                                onClick={isAccordion && !isEditing ? () => toggleMobileCol(fIndex) : undefined}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <Editable
                                                        as="h4"
                                                        isEditing={isEditing}
                                                        value={col.heading || ''}
                                                        onChange={e => update(['footer', fIndex, 'heading'], e.target.value)}
                                                        onStyleChange={(p, v) => handleHeadingStyleChange(fIndex, p, v)}
                                                        currentStyles={{
                                                            align: col.headingAlign,
                                                            size: col.headingSize || col.headingFontSize,
                                                            color: col.headingColor || colHeadingColor,
                                                            fontFamily: col.headingFontFamily,
                                                            preset: col.headingPreset,
                                                            weight: col.headingWeight || col.headingFontWeight,
                                                            fontWeight: col.headingFontWeight || col.headingWeight,
                                                            letterSpacing: col.headingLetterSpacing,
                                                            lineHeight: col.headingLineHeight,
                                                            fontStyle: col.headingFontStyle,
                                                            textDecoration: col.headingTextDecoration,
                                                            transform: col.headingTransform || col.headingTextTransform,
                                                            textTransform: col.headingTextTransform || col.headingTransform,
                                                        }}
                                                        style={{
                                                            ...((_getFontStyles || getFontStyles) ? (_getFontStyles || getFontStyles)('h2', {
                                                                align: col.headingAlign,
                                                                size: col.headingSize || col.headingFontSize,
                                                                color: col.headingColor || colHeadingColor,
                                                                fontFamily: col.headingFontFamily,
                                                                preset: col.headingPreset,
                                                                weight: col.headingWeight || col.headingFontWeight,
                                                                fontWeight: col.headingFontWeight || col.headingWeight,
                                                                letterSpacing: col.headingLetterSpacing,
                                                                lineHeight: col.headingLineHeight,
                                                                fontStyle: col.headingFontStyle,
                                                                textDecoration: col.headingTextDecoration,
                                                                textTransform: col.headingTextTransform || col.headingTransform,
                                                            }) : {}),
                                                            fontSize: (col.headingSize || col.headingFontSize) ? addUnit(col.headingSize || col.headingFontSize) : '1.2rem',
                                                            fontWeight: col.headingWeight || col.headingFontWeight || undefined,
                                                            letterSpacing: col.headingLetterSpacing ? addUnit(col.headingLetterSpacing) : undefined,
                                                            lineHeight: col.headingLineHeight || undefined,
                                                            fontFamily: col.headingFontFamily || settings.footerHeadingFontFamily || 'inherit',
                                                            margin: isAccordion ? 0 : '0 0 10px 0',
                                                        }}
                                                    />
                                                </div>
                                                {isAccordion && (
                                                    <span
                                                        className="footer-accordion-indicator"
                                                        onClick={(e) => { e.stopPropagation(); toggleMobileCol(fIndex); }}
                                                    >
                                                        {isColOpen ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className={`footer-col-body ${isAccordion ? (isColOpen ? 'footer-accordion-body-open' : 'footer-accordion-body-closed') : ''}`}>
                                            <Editable
                                                as="div"
                                                type="richtext"
                                                isEditing={isEditing}
                                                value={col.text || ''}
                                                onChange={e => update(['footer', fIndex, 'text'], e.target.value)}
                                                onStyleChange={(p, v) => handleTextStyleChange(fIndex, p, v)}
                                                currentStyles={{
                                                    align: col.textAlign,
                                                    size: col.textSize || col.textFontSize,
                                                    color: col.textColor || colTextColor,
                                                    fontFamily: col.textFontFamily,
                                                    weight: col.textWeight || col.textFontWeight,
                                                    fontWeight: col.textFontWeight || col.textWeight,
                                                    letterSpacing: col.textLetterSpacing,
                                                    lineHeight: col.textLineHeight,
                                                    fontStyle: col.textFontStyle,
                                                    textDecoration: col.textDecoration,
                                                    transform: col.textTransform || col.textTextTransform,
                                                    textTransform: col.textTextTransform || col.textTransform,
                                                }}
                                                style={{
                                                    opacity: 0.85,
                                                    ...((_getFontStyles || getFontStyles) ? (_getFontStyles || getFontStyles)('body', {
                                                        align: col.textAlign,
                                                        size: col.textSize || col.textFontSize,
                                                        color: col.textColor || colTextColor,
                                                        fontFamily: col.textFontFamily,
                                                        weight: col.textWeight || col.textFontWeight,
                                                        fontWeight: col.textFontWeight || col.textWeight,
                                                        letterSpacing: col.textLetterSpacing,
                                                        lineHeight: col.textLineHeight,
                                                        fontStyle: col.textFontStyle,
                                                        textDecoration: col.textDecoration,
                                                        textTransform: col.textTextTransform || col.textTransform,
                                                    }) : {}),
                                                    fontSize: (col.textSize || col.textFontSize) ? addUnit(col.textSize || col.textFontSize) : '0.9rem',
                                                    fontWeight: col.textWeight || col.textFontWeight || undefined,
                                                    letterSpacing: col.textLetterSpacing ? addUnit(col.textLetterSpacing) : undefined,
                                                    fontFamily: col.textFontFamily || settings.footerBodyFontFamily || 'inherit',
                                                    color: colTextColor,
                                                    lineHeight: col.textLineHeight || '1.6',
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Column Bottom Horizontal Divider */}
                                {col.showHorizontalDivider && (
                                    <div style={{
                                        borderBottom: `${col.horizontalDividerWidth || '1px'} ${col.horizontalDividerStyle || 'solid'} ${col.horizontalDividerColor || dividerColor}`,
                                        marginTop: '16px',
                                        marginBottom: '8px',
                                        width: '100%'
                                    }} />
                                )}
                            </div>
                        );
                    })}

                    {/* ── Add Column button (edit mode only) ── */}
                    {isEditing && (
                        <div style={{ flex: '1 1 100%', width: '100%', padding: '12px 0 16px 0', display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={() => handleAddCol('text')}
                                style={{
                                    background: 'rgba(59,130,246,0.2)',
                                    color: '#93c5fd',
                                    border: '1px dashed #3b82f6',
                                    borderRadius: '6px',
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                + Add Column
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Global Footer Horizontal Dividing Line ── */}
            {settings?.showFooterHorizontalDivider && (
                <div className="container" style={{ padding: '0 20px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <hr style={{
                        border: 'none',
                        borderTop: `${settings.footerHorizontalDividerWidth || '1px'} ${settings.footerHorizontalDividerStyle || 'solid'} ${settings.footerHorizontalDividerColor || 'rgba(255,255,255,0.15)'}`,
                        margin: settings.footerHorizontalDividerMargin || '24px 0',
                        width: '100%'
                    }} />
                </div>
            )}

            {/* ── Social Icons (Bottom Bar) ── */}
            {settings?.showFooterBottomSocials !== false && (
                <div className="footer-socials" style={{
                    textAlign: 'center',
                    marginTop: settings?.showFooterHorizontalDivider ? '10px' : '30px',
                    display: 'flex',
                    gap: '15px',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 1,
                }}>
                    {(socials || []).map((s, i) => (
                        <a
                            key={i}
                            href={s.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                color: 'white',
                                textDecoration: 'none',
                                background: 'rgba(255,255,255,0.1)',
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid rgba(255,255,255,0.2)',
                                transition: 'all 0.2s',
                            }}
                            onMouseOver={e => {
                                if (settings.primaryColor) e.currentTarget.style.background = settings.primaryColor;
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={e => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                e.currentTarget.style.transform = 'none';
                            }}
                        >
                            {renderIcon(s.platform, 20)}
                        </a>
                    ))}
                </div>
            )}

            {/* ── Bottom Bar: Copyright, Legal & Admin Login / Status ── */}
            <div className="footer-bottom-bar" style={{
                textAlign: 'center',
                marginTop: '40px',
                fontSize: settings.footerCopyrightFontSize ? addUnit(settings.footerCopyrightFontSize) : '0.85em',
                color: settings.footerCopyrightColor || 'rgba(255,255,255,0.6)',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                paddingTop: '20px',
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Copyright Text (Inline Editable) */}
                <div className="footer-copyright" style={{ marginBottom: '12px' }}>
                    {isEditing ? (
                        <Editable
                            as="span"
                            isEditing={true}
                            value={copyrightText}
                            onChange={e => update(['settings', 'footerCopyrightText'], e.target.value)}
                            style={{
                                border: '1px dashed rgba(255,255,255,0.3)',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                cursor: 'text',
                                color: settings.footerCopyrightColor || 'inherit',
                                display: 'inline-block',
                            }}
                        />
                    ) : (
                        <span>{copyrightText}</span>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                    <a href="https://omnivaso.com/why-omnivaso" target="_blank" rel="noopener noreferrer" style={{ color: settings.footerLinkColor || '#93c5fd', textDecoration: 'none', fontWeight: '500' }} onClick={isEditing ? (e) => e.preventDefault() : undefined}>⚡ Why OMNIVASO</a>
                    <span>•</span>
                    {(() => {
                        const isDemo = typeof window !== 'undefined' && localStorage.getItem('omnivaso_demo_mode') === 'true';
                        if (isDemo) {
                            return (
                                <button
                                    type="button"
                                    onClick={() => {
                                        try {
                                            const tenantKey = typeof getTenantStorageKey === 'function' ? getTenantStorageKey('cached_config') : null;
                                            if (tenantKey) localStorage.removeItem(tenantKey);
                                            localStorage.removeItem('omnivaso_cached_config');
                                            localStorage.removeItem('adminToken');
                                            localStorage.removeItem('userRole');
                                            localStorage.removeItem('loggedInUser');
                                            localStorage.removeItem('omnivaso_demo_mode');
                                            localStorage.removeItem('omnivaso_demo_draft');
                                            localStorage.removeItem('omnivaso_live_edit_auto');
                                            sessionStorage.clear();
                                            if (typeof document !== 'undefined') {
                                                document.documentElement.style.removeProperty('--demo-bar-height');
                                                document.documentElement.style.setProperty('--demo-bar-height', '0px');
                                                document.body.style.paddingTop = '0px';
                                                document.body.style.removeProperty('padding-top');
                                            }
                                        } catch (e) {}
                                        if (typeof window !== 'undefined') {
                                            window.location.replace(window.location.origin + window.location.pathname + window.location.hash);
                                        }
                                    }}
                                    style={{
                                        background: '#ef4444',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '4px 10px',
                                        fontSize: '0.8rem',
                                        fontWeight: '700',
                                        cursor: 'pointer'
                                    }}
                                >
                                    🚪 Logout (Exit Demo)
                                </button>
                            );
                        }
                        if (settings.hideFooterAdminLogin) return null;
                        return isAuthenticated
                            ? <span style={{ color: '#10b981' }}>● Logged In</span>
                            : <Link to={normalizeAppUrl('/login')} style={{ color: settings.footerTextColor || 'white', textDecoration: 'none' }}>Admin Login</Link>;
                    })()}
                </div>

                {(!settings.hideCurrencySelector && !settings.currencySelectorDisabled && (isEcommerceUnlocked(config) || settings.currencyEngineEnabled === true || settings.enableMultiCurrency === true) && settings.currencySelectorPlacement === 'footer') && (
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
                        <CurrencySelector settings={settings} />
                    </div>
                )}
            </div>

        </footer>
        <ReturnToTop settings={settings || config?.settings || {}} />
        </>
    );
};

export default SiteFooter;
